/**
 * Arayüzdeki tüm Türkçe metinler burada. JSX içine düz metin gömülmez (AGENTS.md §6).
 *
 * Not: Anlamı bilinmeyen kaynak kolonları (`tip_kodu`, `durum_kodu`, `sevk_tip_kodu`,
 * `bekleme`, `etiket1..3`) burada **etiketlenmez**; başlık olarak ham kolon adı kullanılır.
 */
export const messages = {
  app: {
    name: 'betontasdash',
    shortName: 'btd',
  },

  common: {
    retry: 'Tekrar dene',
    refresh: 'Yenile',
    save: 'Kaydet',
    saving: 'Kaydediliyor…',
    cancel: 'Vazgeç',
    close: 'Kapat',
    duration: { day: 'gün', hour: 'sa', minute: 'dk' },
    scrollTabsLeft: 'Sekmeleri sola kaydır',
    scrollTabsRight: 'Sekmeleri sağa kaydır',
    clearSearch: 'Aramayı temizle',
    create: 'Oluştur',
    edit: 'Düzenle',
    delete: 'Sil',
    search: 'Ara',
    apply: 'Uygula',
    clear: 'Temizle',
    copy: 'Kopyala',
    copied: 'Kopyalandı',
    columns: 'Kolonlar',
    filter: 'Filtre',
    rowNumber: 'Satır no',
    resetColumns: 'Sütunları sıfırla',
    resizeColumn: 'Kolon genişliğini ayarla',
    resizeColumnHint: 'Sürükleyerek genişliği ayarla; çift tık varsayılana döndürür',
    /** Kolon başlığı sürüklenirken ekran okuyucu duyuruları (dnd-kit). */
    columnDrag: {
      role: 'taşınabilir kolon',
      handle: (column: string) => `${column} kolonunu taşı`,
      instructions:
        'Kolonu taşımak için boşluk tuşuna basın, ok tuşlarıyla yerini değiştirin, bırakmak için tekrar boşluk, vazgeçmek için Esc.',
      start: (column: string) => `${column} kolonu tutuldu.`,
      over: (column: string, target: string) => `${column} kolonu ${target} üzerinde.`,
      end: (column: string) => `${column} kolonu bırakıldı.`,
      cancel: (column: string) => `${column} kolonunu taşıma iptal edildi.`,
    },
    actions: 'İşlemler',
    exportExcel: "Excel'e aktar",
    exportEmpty: 'Aktarılacak veri yok',
    exportPending: 'Hazırlanıyor…',
    exportFailed: 'Aktarılamadı, tekrar deneyin',
    all: 'Tümü',
    selectAll: 'Tümünü seç',
    clearSelection: 'Seçimi kaldır',
    yes: 'Evet',
    no: 'Hayır',
    active: 'Etkin',
    inactive: 'Pasif',
    none: 'Yok',
    empty: '—',
    unknown: 'Bilinmiyor',
    status: 'Durum',
    selected: 'seçili',
    openMenu: 'Menüyü aç',
    userMenu: 'Kullanıcı menüsü',
    mainNavigation: 'Ana gezinme',
    toggleSidebar: 'Kenar menüyü aç/kapat',
  },

  calendar: {
    today: 'Bugün',
    previousMonth: 'Önceki ay',
    nextMonth: 'Sonraki ay',
    pickDate: 'Tarih seç',
  },

  alert: {
    dismiss: (title: string) => `Kapat: ${title}`,
  },

  toast: {
    region: 'Bildirimler',
    dismiss: 'Bildirimi kapat',
    types: {
      success: 'Başarılı',
      info: 'Bilgi',
      warning: 'Uyarı',
      error: 'Hata',
      loading: 'Sürüyor',
    },
  },

  theme: {
    label: 'Tema',
    light: 'Açık',
    dark: 'Koyu',
    system: 'Sistem',
    toggle: 'Temayı değiştir',
  },

  pagination: {
    rowsPerPage: 'Sayfa başına satır',
    pageOf: (page: number, pageCount: number) => `Sayfa ${page} / ${pageCount}`,
    totalRows: (total: number) => `${total} kayıt`,
    page: (page: number) => `Sayfa ${page}`,
    navLabel: 'Sayfalama',
    first: 'İlk sayfa',
    previous: 'Önceki sayfa',
    next: 'Sonraki sayfa',
    last: 'Son sayfa',
  },

  /** Veri ızgarası: hücre seçimi ve filtre satırı (components/data-table). */
  grid: {
    cellsSelected: (count: number) => `${count} hücre seçili`,
    selectionSum: 'Toplam',
    /** Altta sabit toplam satırının etiketi (gutter ve ilk sütun). */
    totalRow: 'Toplam',
    copied: 'Panoya kopyalandı',
    clearFilters: 'Temizle',
    expandRow: 'Satırı aç',
    collapseRow: 'Satırı kapat',
    filterRow: {
      contains: (column: string) => `${column}: içerir`,
      from: (column: string) => `${column}: başlangıç`,
      to: (column: string) => `${column}: bitiş`,
      containsPlaceholder: 'İçerir…',
      minPlaceholder: 'En az',
      maxPlaceholder: 'En çok',
      fromDatePlaceholder: 'Başlangıç',
      toDatePlaceholder: 'Bitiş',
      edit: (column: string, summary: string) =>
        `${column}: ${summary} — değiştirmek için tıklayın`,
    },
  },

  /** Düzenlenebilir ızgara (components/grid; `grid` anahtarı salt-okunur DataTable'ınki). */
  editableGrid: {
    rowHeader: 'Satır',
    selectRow: (rowNumber: number) => `${rowNumber}. satırı seç`,
    rowHasIssue: 'Satırda hatalı hücre var',
    openList: 'Listeyi aç',
    listPlaceholder: 'Seçin…',
    emptyOption: '—',
    noResults: 'Sonuç yok',
  },

  states: {
    loading: 'Yükleniyor…',
    emptyTitle: 'Kayıt yok',
    emptyHint: 'Seçili filtrelerle eşleşen kayıt bulunamadı.',
    errorTitle: 'Veri alınamadı',
    errorHint: 'İstek tamamlanamadı. Tekrar deneyebilirsiniz.',
    forbiddenTitle: 'Bu sayfaya erişim yetkiniz yok',
    forbiddenHint:
      'Görmeniz gerektiğini düşünüyorsanız yöneticinizden ilgili gruba eklenmenizi isteyin.',
    notFoundTitle: 'Sayfa bulunamadı',
    notFoundHint: 'Adres yanlış olabilir.',
    goHome: 'Başlangıç sayfasına dön',
  },

  realtime: {
    live: 'Canlı',
    connecting: 'Bağlanıyor…',
    reconnecting: 'Yeniden bağlanıyor…',
    offline: 'Bağlantı yok',
    lastEventLabel: 'Son güncelleme',
    noEventYet: 'Henüz güncelleme yok',
    title: 'Canlı veri bağlantısı',
  },

  auth: {
    loginTitle: 'Oturum aç',
    loginSubtitle: 'Devam etmek için hesap bilgilerinizi girin.',
    email: 'E-posta',
    emailPlaceholder: 'ad.soyad@ornek.com',
    password: 'Parola',
    currentPassword: 'Mevcut parola',
    newPassword: 'Yeni parola',
    newPasswordRepeat: 'Yeni parola (tekrar)',
    submit: 'Giriş yap',
    submitting: 'Giriş yapılıyor…',
    logout: 'Çıkış yap',
    changePassword: 'Parola değiştir',
    changePasswordTitle: 'Parola değiştirme',
    changePasswordForced: 'Devam etmeden önce parolanızı değiştirmeniz gerekiyor.',
    changePasswordDone: 'Parola güncellendi.',
    sessions: 'Oturumlar',
    sessionsTitle: 'Etkin oturumlar',
    revokeSession: 'Oturumu sonlandır',
    errors: {
      emailRequired: 'E-posta girin.',
      emailInvalid: 'Geçerli bir e-posta girin.',
      passwordRequired: 'Parola girin.',
      passwordTooShort: 'Parola en az 8 karakter olmalı.',
      passwordMismatch: 'Parolalar eşleşmiyor.',
      invalidCredentials: 'E-posta veya parola hatalı.',
      sessionExpired: 'Oturum süresi doldu, tekrar giriş yapın.',
      generic: 'Giriş yapılamadı.',
    },
  },

  /** Claude bağlantısı onay ekranı (`/oauth/mcp`, docs/MCP-PLAN.md). */
  mcpConsent: {
    title: 'Claude bağlantısı',
    request: (client: string) =>
      `${client}, betontasdash verilerinize sizin hesabınız ve yetkilerinizle erişmek istiyor.`,
    signedInAs: (email: string) => `Oturum: ${email}`,
    canTitle: 'Bu bağlantıyla Claude:',
    canViews: 'Erişebildiğiniz raporların listesini ve alanlarını görür.',
    canQuery:
      'Sorgu sonuçlarını alır (yalnız "Claude\'a sorgu sonucu satırları gönderme" izniniz varsa).',
    cannotWrite: 'Hiçbir veriyi değiştiremez.',
    revokeHint: 'Bağlantı, Claude bağlantısı izniniz kaldırıldığında kendiliğinden kapanır.',
    noPermission:
      'Claude bağlantısı için yetkiniz yok. Yöneticinizden "Claude bağlantısı" iznini isteyin.',
    missingRequest: 'Bağlantı isteği bulunamadı. Claude üzerinden yeniden bağlanın.',
    approve: 'İzin ver',
    approving: 'Yönlendiriliyor…',
    deny: 'Reddet',
  },

  /** Cube meta'sından view/üye seçici (`components/member-picker`). */
  memberPicker: {
    view: 'Görünüm',
    measure: 'Ölçü',
    dimension: 'Boyut',
    time: 'Zaman boyutu',
    none: 'Seçilmedi',
    searchPlaceholder: 'Ara…',
    empty: 'Sonuç yok',
    add: 'Ekle',
    notFound: (value: string) => `${value} (bulunamadı)`,
    selectedList: (label: string) => `${label}: seçilenler`,
    moveUp: (label: string) => `${label} üyesini yukarı taşı`,
    moveDown: (label: string) => `${label} üyesini aşağı taşı`,
    remove: (label: string) => `${label} üyesini kaldır`,
  },

  nav: {
    groupCash: 'Nakit',
    cashWeekly: 'Haftalık Nakit',
    cashPlanning: 'Ödeme Planlama',
    cashManual: 'ERP Dışı Kalemler',
    cashSettings: 'Nakit Ayarları',
    cashCheques: 'Çek Portföyü',
    cashHealth: 'Veri Sağlığı',
    cashHome: 'Bu Hafta',
    cashPaymentList: 'Ödeme Listesi',
    cashParallel: 'Paralel Koşum',
    cashTodoCount: (n: number) => `${n} bekleyen iş`,
    cashVariance: 'Plan / Gerçekleşen',
    groupDocuments: 'Belgeler',
    documentTemplates: 'Raporlar',
    groupSystem: 'Sistem',
    groupAdmin: 'Yönetim',
    systemStatus: 'Sistem Durumu',
    adminUsers: 'Kullanıcılar',
    adminGroups: 'Gruplar',
    adminAudit: 'Denetim Kaydı',
    adminSources: 'Veri Kaynakları',
    adminSections: 'Yönetim bölümleri',
    adminOverview: 'Genel bakış',
    groupHr: 'İnsan Kaynakları',
    hrPersonnel: 'Personel',
    hrAttendance: 'Giriş/Çıkış',
    hrLeaves: 'İzinler',
    hrApprovals: 'Onaylar',
    hrApprovalsAwaiting: (count: number) => `${count} talep onayınızı bekliyor`,
    hrApprovalsRevisions: (count: number) => `Revizyonda ${count}`,
    hrProcesses: 'Süreçler',
    hrInterventions: 'Müdahaleler',
    hrTasks: 'İşlerim',
    search: 'Ara…',
    searchPlaceholder: 'Sayfa veya rapor ara…',
    searchEmpty: 'Sonuç yok.',
  },

  /** İzin katalogu — sözleşme §4'teki açıklamaların birebir karşılığı. */
  permissions: {
    'report.tartim:view': 'Tartım Kayıtları ekranı',
    'report.canli_tartim:view': 'Canlı Tartım ekranı',
    'report.tonaj:view': 'Kantar Raporu ekranı',
    'report.rpr183:edit': 'Kantar – DİA Mutabakatı kayıt düzeltme ve açıklama',
    'report.rpr183:view': 'Kantar – DİA Mutabakatı ekranı',
    'report.ebanka:view': 'Banka Hareketleri ekranı',
    'report.cek:view': 'Çek Listesi ekranı',
    'report.kredi_karti:view': 'Kredi Kartları ekranı',
    'report.pos:view': 'POS Tahsilatları ekranı',
    'report.maliyet:view': 'Maliyet ekranı',
    'report.documents:view': 'Belgeler ekranı',
    'report.publish': 'Rapor paylaşma ve paylaşılan raporları düzenleme',
    'system.status:view': 'Sistem Durumu ekranı',
    'admin.users:manage': 'Kullanıcı yönetimi',
    'admin.groups:manage': 'Grup ve izin yönetimi',
    'admin.audit:view': 'Denetim kaydı',
    'admin.sources:view': 'Veri kaynakları ve bağlantı sağlığı',
    'ai.use': 'Claude bağlantısı (rapor listesi ve alanları)',
    'ai.data:send': "Claude'a sorgu sonucu satırları gönderme",
    'hr.personnel:view': 'Personel ekranı',
    'hr.attendance:view': 'Giriş/çıkış ekranı',
    'hr.leave:view': 'İzin ekranı',
    'hr.action:run': 'Kayıt aksiyonları ve süreçleri şimdi çalıştırma',
    'hr.request:create': 'Elle giriş/çıkış talebi açma',
    'hr.request:approve': 'Giriş/çıkış talebi onay, revizyon, iptal ve onaysız işleme',
    'hr.personnel:deactivate': 'Personeli pasife alma',
    'cash.view': 'Nakit planı ekranları',
    'cash.plan': 'Nakit planı planlama ve elle kalem girişi',
    'cash.approve': 'Ödeme listesi onayı',
    'cash.settings': 'Nakit planı ayarları ve işlem türü sınıfları',
    'cash.entry': 'Nakit planı ERP dışı kalem ve tahsilat girişi',
  } as Record<string, string>,

  filters: {
    title: 'Filtreler',
    more: (count: number) => `Diğer filtreler (${count})`,
    fewer: 'Daha az filtre',
    activeCount: (count: number) => `${count} etkin`,
    dateFrom: 'Başlangıç tarihi',
    dateTo: 'Bitiş tarihi',
    timeRangeInvalid: 'Başlangıç saati bitiş saatinden sonra olamaz; saat uygulanmadı.',
    hour: 'Saat',
    minute: 'Dakika',
    timeFrom: 'Başlangıç saati',
    timeTo: 'Bitiş saati',
    apply: 'Uygula',
    rangePresets: 'Hazır aralıklar',
    pickStart: 'Takvimden başlangıç gününü seçin.',
    pickEnd: 'Şimdi bitiş gününü seçin.',
    dateRange: 'Tarih aralığı',
    searchPlaceholder: 'Ara…',
    reset: 'Filtreleri temizle',
    presetsRegion: 'Hazır aralıklar',
    rangeNone: 'Tarih seçilmedi',
    rangeDays: (n: number) => `${n} gün`,
    pickEndDate: 'Bitiş günü seçin',
    rangeStarted: (start: string) => `Başlangıç ${start}. Bitiş gününü seçin.`,
    selectOptions: 'Seçenekleri işaretleyin',
    clearSelection: 'Seçimi temizle',
    selectedCount: (n: number) => `${n} seçili`,
    noOption: 'Seçenek yok',
    dateRangeInvalid: 'Başlangıç tarihi bitiş tarihinden sonra olamaz.',
    range: {
      today: 'Bugün',
      yesterday: 'Dün',
      all: 'Tümü',
      last7: 'Son 7 gün',
      last30: 'Son 30 gün',
      last90: 'Son 90 gün',
      thisMonth: 'Bu ay',
      lastMonth: 'Geçen ay',
      custom: 'Özel',
      /** Göreli aralık etiketi: `-1y` -> "Son 1 yıl", `+2y` -> "Önümüzdeki 2 yıl". */
      relative: (token: string) => {
        const unit = { d: 'gün', m: 'ay', y: 'yıl' }[token.at(-1) ?? 'd']
        return `${token.startsWith('-') ? 'Son' : 'Önümüzdeki'} ${token.slice(1, -1)} ${unit}`
      },
    },
    /** Excel tarzı sütun filtresi (bkz. components/filters/ColumnFilterPopover.tsx). */
    column: {
      open: (column: string) => `${column} filtresi ve sıralaması`,
      remove: (column: string) => `${column} filtresini kaldır`,
      sortAsc: 'A → Z',
      sortDesc: 'Z → A',
      sortAscHint: 'Artan sırala (sonraki öncelik olarak eklenir)',
      sortDescHint: 'Azalan sırala (sonraki öncelik olarak eklenir)',
      sortRemove: 'Sıralamadan çıkar',
      sortPriority: (n: number) => `${n}. öncelik`,
      clearSort: 'Sıralamayı temizle',
      values: 'Değerler',
      conditions: 'Koşullar',
      selectAll: '(Tümünü seç)',
      selectAllResults: '(Tüm arama sonuçlarını seç)',
      blank: '(Boş)',
      searchValues: 'Değer ara…',
      noValues: 'Değer bulunamadı.',
      loadingValues: 'Değerler yükleniyor…',
      moreValues: 'Listede ilk değerler var; aradığınızı yazarak daraltın.',
      join: 'Koşulları bağla',
      and: 'VE',
      or: 'VEYA',
      operator: 'Operatör',
      value: 'Değer',
      valueTo: 'Bitiş değeri',
      addCondition: 'Koşul ekle',
      removeCondition: 'Koşulu kaldır',
      apply: 'Uygula',
      clear: 'Filtreyi temizle',
      conditionCount: (n: number) => `${n} koşul`,
      valueCount: (n: number) => `${n} değer`,
      excludedCount: (n: number) => `${n} değer hariç`,
      operators: {
        eq: 'Eşittir',
        neq: 'Eşit değildir',
        contains: 'İçerir',
        notContains: 'İçermez',
        startsWith: 'İle başlar',
        endsWith: 'İle biter',
        gt: 'Büyüktür',
        gte: 'Büyük veya eşittir',
        lt: 'Küçüktür',
        lte: 'Küçük veya eşittir',
        between: 'Arasında',
        set: 'Boş değil',
        notSet: 'Boş',
      },
    },
  },

  /** Dönem filtresi (`lib/period.ts`). */
  period: {
    months: [
      'Ocak',
      'Şubat',
      'Mart',
      'Nisan',
      'Mayıs',
      'Haziran',
      'Temmuz',
      'Ağustos',
      'Eylül',
      'Ekim',
      'Kasım',
      'Aralık',
    ],
    placeholder: 'Ay seçin',
    previousYear: 'Önceki yıl',
    nextYear: 'Sonraki yıl',
  },

  /** Meta'dan kurulan rapor sayfalarının ortak metinleri (bkz. features/report). */
  report: {
    granularity: 'Kırılım',
    granularities: {
      day: 'Gün',
      week: 'Hafta',
      month: 'Ay',
      quarter: 'Çeyrek',
      year: 'Yıl',
    },
    bucketCount: {
      day: 'Gün sayısı',
      week: 'Hafta sayısı',
      month: 'Ay sayısı',
      quarter: 'Çeyrek sayısı',
      year: 'Yıl sayısı',
    },
    quarterLabel: (year: string, quarter: number) => `${year} Ç${quarter}`,
    compare: 'Önceki dönemle karşılaştır',
    compareNeedsRange: 'Karşılaştırma için bir tarih aralığı seçin.',
    previousPeriod: (value: string) => `Önceki dönem: ${value}`,
    tableTitle: 'Grafikteki veri',
    definitionErrors: (count: number) =>
      `${count} rapor tanımı geçersiz, menüde gösterilmiyor (yalnızca superadmin görür):`,
    total: 'Toplam',
    share: (label: string) => `${label} toplamdaki payı`,
    fxRate: (code: string, date: string, rate: string) =>
      `TCMB ${code} döviz satış (${date}): ${rate}`,
    fxUnavailable: 'Kur alınamadı',
    breakdownPattern: (solid: string, striped: string) =>
      `Düz çubuk: ${solid} · Taralı çubuk: ${striped}`,
    breakdownOther: 'Diğer',
    breakdownChoice: 'Kırılım',
    drillHint: 'Kaynak satırları gör',
    drillLabel: (label: string) => `${label}: kaynak satırları gör`,
    drillDescription: 'Bu sayıyı oluşturan kayıtlar, sayfadaki filtrelerle.',
  },

  aging: {
    modeLabel: 'Yaşlandırma kırılımı',
    modes: { day: 'Günlük', month: 'Aylık' },
    overdue: 'Vadesi geçmiş',
    dayRange: (from: number, to: number) => `${from}-${to}`,
    total: 'Toplam',
    totalLabel: (currency: string | undefined, count: number) =>
      currency ? `Toplam ${currency} (${count} kayıt)` : `Toplam (${count} kayıt)`,
  },

  crossFilter: {
    clear: 'Seçimleri temizle',
    remove: (label: string) => `${label} seçimini kaldır`,
    selectValue: (label: string) => `${label} değerine göre süz`,
  },

  reconcile: {
    needsKantar:
      'Bu rapor kantar tartımlarını da okur: Canlı Tartım izni (report.canli_tartim:view) gerekli.',
    truncated: (limit: string) =>
      `Sonuç ${limit} satırda kesildi; mutabakat eksik olabilir. Tarih aralığını daraltın.`,
    kpi: {
      total: 'Toplam tartı',
      ok: 'Uyumlu',
      error: 'Hatalı',
      warning: 'Uyarılı',
      onsite: 'Sahada',
      waiting: 'İrsaliye bekleniyor',
      deleted: 'Silindi',
    },
    status: {
      ok: 'Uyumlu',
      error: 'Hatalı',
      warning: 'Uyarılı',
      onsite: 'Sahada',
      waiting: 'İrsaliye bekleniyor',
      deleted: 'Silindi',
    },
    codes: {
      'H-A': 'İrsaliye kesilmemiş',
      'H-B': 'Kantar kaydı yok',
      'H-C': 'Tonaj farkı',
      'H-D': 'Cari farkı',
      'H-E': 'Stok farkı',
      'H-F': 'Plaka farkı',
      'U-G': 'Sipariş farkı',
      'U-H': 'Şoför / nakliyeci farkı',
      'U-I': 'Tarih farkı',
      'U-J': 'Nakliye tutarsızlığı',
      'H-K': 'Mükerrer kayıt',
      'H-L': 'Çıkış tartımı yapılmamış',
      'U-M': 'Nakliyeci Mal Alımı yok',
    },
    noIssues: 'Hata veya uyarı yok.',
    onsiteFor: (duration: string) => `Girişten beri ${duration}`,
    prefixTitle: 'Ön ek kontrolü',
    prefixUnknown: (kantar: string, values: string) =>
      `${kantar}: beklenen ön ek tanımlı değil; DİA'da görülen (ön ek / firma): ${values}`,
    columns: {
      kantar: 'Kantar',
      tartino: 'Tartı No',
      plaka: 'Plaka',
      cari: 'Cari',
      kantarValue: 'Kantar Değeri',
      diaValue: 'DİA Değeri',
      diff: 'Fark',
      kantarTon: 'Kantar Net (t)',
      diaTon: 'DİA Miktar (t)',
      status: 'Durum',
      codes: 'Hata Kodları',
      giris: 'Giriş',
      cikis: 'Çıkış',
      note: 'Açıklama',
      expected: 'Beklenen (ön ek / firma)',
      actual: 'DİA (ön ek / firma)',
      tarih: 'Tarih',
      diffKg: 'Fark (kg)',
      tartiCount: 'Tartı',
    },
    kpiFilter: (label: string, active: boolean) =>
      active ? `${label}: süzme kaldır` : `${label}: yalnız bunları göster`,
    tonnage: {
      title: 'Tonaj karşılaştırması',
      description:
        'Kantar neti (çıkış − giriş) ile DİA irsaliyesindeki malzeme (MLZM) miktarının toplamı.',
      kantar: 'Kantar net (ton)',
      dia: 'DİA MLZM (ton)',
      diff: 'Fark: DİA − Kantar (ton)',
      count: (count: string) => `${count} tartı`,
      withoutTonnage: (count: string, all: string) =>
        `Toplam ${all} tartıdan ${count} tanesi bu karşılaştırmaya girmez: ne kantar neti ne DİA miktarı var (ör. çıkış tartımı yapılmamış sahadaki araçlar, kantarda silinmiş tartılar).`,
      balanced: 'Kantar ve DİA tonajı tutuyor.',
      matchedOk: (count: string, ton: string) =>
        `Her iki tarafta da olan ${count} tartı (${ton} ton): tonaj birebir tutuyor.`,
      matchedOff: (count: string, ton: string) =>
        `Her iki tarafta da olan ${count} tartıda ${ton} ton fark var (H-C Tonaj farkı).`,
      kantarOnlyCause: (count: string, ton: string) =>
        `${count} tartı yalnız kantarda (${ton} ton): DİA’da irsaliyesi yok (H-A İrsaliye kesilmemiş ya da İrsaliye bekleniyor).`,
      diaOnlyCause: (count: string, ton: string) =>
        `${count} tartı yalnız DİA’da (${ton} ton): kantar neti yok (H-B Kantar kaydı yok).`,
    },
    tabs: {
      records: 'Kayıtlar',
      kantars: 'Kantarlar',
      guide: 'Kod açıklamaları',
    },
    // Kurallar lib/kantar-dia.ts'te; değişirse bu metinler de güncellenmeli.
    guide: {
      errors: 'Hata kodları (H-)',
      errorsHint: 'Bu kodlardan biri olan tartı Hatalı sayılır.',
      warnings: 'Uyarı kodları (U-)',
      warningsHint: 'Yalnız bu kodlar olan tartı Uyarılı sayılır.',
      statuses: 'Durumlar',
      codes: {
        'H-A':
          "Kantarda çıkış tartımı yapılmış ama DİA'da bu tartıya irsaliye yok ya da irsaliyede malzeme (MLZM) satırı yok. Çıkıştan sonra ilk 30 dakika İrsaliye bekleniyor sayılır (DİA verisi 15 dakikada bir çekilir). Kantarın DİA aktarımı başarısızsa beklenmez, DİA'nın hata mesajı notta yazar; kantar aktardığı hâlde DİA'da yoksa notta bu belirtilir. Kantarda süreci Belirsiz olan irsaliyesiz tartılar da buraya girer.",
        'H-B':
          'DİA’da bu tartı numarasına irsaliye var ama kantarda kayıt yok ya da kantar kaydı tamamlanmamış (Sahada, Silindi, Belirsiz).',
        'H-C':
          'Kantar neti (çıkış − giriş) ile DİA malzeme (MLZM) miktarı farklı. 1 kg fark da hata sayılır.',
        'H-D': 'Kantardaki cari kodu ile DİA irsaliyesindeki cari kodu farklı.',
        'H-E': 'Kantardaki stok kodu ile DİA irsaliyesindeki stok kodu farklı.',
        'H-F':
          'Kantardaki plaka ile DİA irsaliyesindeki plaka farklı. Boşluk ve büyük/küçük harf farkı sayılmaz.',
        'H-K':
          'Mükerrer kayıt: tartıya birden fazla malzeme (MLZM) satırı kesilmiş ya da aynı irsaliye birden fazla tartıda kullanılmış.',
        'H-L':
          'Çıkış tartımı yapılmamış, kayıt kantarda açık kalmış: girişten bu yana 24 saatten fazla geçti ya da aynı araç (aynı kantar ve plaka) sonradan yeniden tartılıp çıktı ve kayıt 6 saatten uzun süredir açık.',
        'U-G': 'Kantardaki sipariş numarası ile DİA irsaliyesindeki sipariş belge numarası farklı.',
        'U-H':
          'Şoför ya da nakliyeci adı farklı. Harf ve boşluk farkı sayılmaz; kantarda “Nakliye Kendine Ait” yazıp DİA’da nakliyeci boşsa fark sayılmaz.',
        'U-I':
          'Kantar tarihi ile irsaliye tarihi arasında 1 günden fazla fark var. 1 güne kadar fark normal kabul edilir (gece yarısına sarkan irsaliye).',
        'U-J':
          'Nakliye satırları (HZMT / Mal Alım) kantarla uyumsuz: miktarı malzemeden farklı; nakliye kendine ait ama nakliye satırı var; şirket aracı ama Mal Alım satırı var; dış nakliyecide Mal Alım carisi nakliyeciyle aynı değil (Mal Alım hiç yoksa U-M).',
        'U-M':
          "Kantarda dış bir nakliyeci var (Nakliye Kendine Ait ya da şirket aracı değil) ama DİA'da bu tartıya nakliyecinin Mal Alım satırı yok. Tartı no'su girilmemiş toplu Mal Alım satırları tartıya bağlanamaz. Kantarda nakliyeci boşsa kontrol edilemez.",
      },
      manualHint:
        'Kalem simgeli kayıtların durumu yetkili bir kullanıcı tarafından elle verilmiştir; açıklaması Açıklama kolonunda ve detay panelinde. Kuralların bulduğu durum ve hata kodları detayda görünmeye devam eder.',
      statusHelp: {
        error: 'En az bir hata kodu (H-) var.',
        warning: 'Hata yok, en az bir uyarı kodu (U-) var.',
        onsite:
          'Araç sahada, çıkış tartımı henüz yapılmamış. Girişten beri geçen süre yanında yazar, 2 saatten sonra kırmızı. 24 saati aşarsa ya da araç yeniden tartılırsa H-L.',
        waiting:
          "İrsaliye henüz DİA'da görünmüyor: çıkıştan bu yana 30 dakika dolmadı (DİA verisi 15 dakikada bir çekilir).",
        ok: 'Kantar ve DİA birebir tutuyor.',
        deleted: 'Tartı kantarda silinmiş, DİA’da da irsaliyesi yok. Tonaja girmez.',
      },
    },
    filter: {
      search: 'Ara',
      searchPlaceholder: 'Tartı no, plaka, cari',
      kantar: 'Kantar',
      timeNote:
        'Saat, tamamlanan tartımın çıkış (2. tartım) zamanına uygulanır: tonaj ve irsaliye çıkışta kesinleşir. Çıkışı olmayan (sahadaki) araçlar giriş saatine göre süzülür. Kantar kaydı olmayan DİA satırlarının saati yoktur, her zaman listelenir.',
      shown: (shown: string, total: string) => `${shown} / ${total} kayıt`,
      empty: 'Filtreye uyan kayıt yok.',
      statusActive: (status: string) => `Yalnız “${status}” gösteriliyor`,
      showAll: 'Tümünü göster',
    },
    mobile: {
      more: (count: string, rest: string) => `${count} tane daha göster (${rest} kaldı)`,
      tonnage: (kantar: string, dia: string, diff: string) =>
        `Kantar ${kantar} · DİA ${dia} · Fark ${diff}`,
      times: (giris: string, cikis: string) => `Giriş ${giris} · Çıkış ${cikis}`,
    },
    exportFilename: 'kantar-dia-mutabakat',
    override: {
      title: 'Elle düzeltme',
      marker: (who: string) => `Elle düzeltildi (${who})`,
      by: (who: string, when: string) => `Son değiştiren: ${who} · ${when}`,
      computed: 'Kuralların bulduğu durum:',
      status: 'Durum',
      keepStatus: 'Değiştirme (yalnız açıklama)',
      note: 'Açıklama',
      notePlaceholder: 'Neden düzeltildiğini yazın',
      save: 'Kaydet',
      remove: 'Düzeltmeyi kaldır',
      removeConfirm:
        'Elle düzeltme kaldırılsın mı? Durum yeniden kurallara göre hesaplanır, açıklama silinir.',
      saved: 'Kaydedildi.',
      failed: 'Kaydedilemedi, tekrar deneyin.',
      none: 'Bu kayıt için elle düzeltme yok.',
      noTartino: 'Tartı numarası olmayan kayıt düzeltilemez.',
    },
    detail: {
      issues: 'Hata ve uyarılar',
      compare: 'Kantar – DİA karşılaştırması',
      field: 'Alan',
      kantar: 'Kantar',
      dia: 'DİA (MLZM)',
      weighing: 'Tartım',
      lines: (count: number) => `DİA satırları – ${count}`,
      noLines: 'Bu tartı için DİA satırı yok.',
      noKantar: 'Bu tartı için kantar kaydı yok.',
      fields: {
        tarih: 'Tarih',
        cariKod: 'Cari kodu',
        cari: 'Cari',
        stokKod: 'Stok kodu',
        plaka: 'Plaka',
        sofor: 'Şoför',
        nakliyeci: 'Nakliyeci',
        siparis: 'Sipariş',
        net: 'Net / miktar (ton)',
        surec: 'Süreç durumu',
        giris: 'Giriş',
        cikis: 'Çıkış',
        girisKg: 'Giriş (kg)',
        cikisKg: 'Çıkış (kg)',
      },
      lineColumns: {
        irsaliyeNo: 'İrsaliye No',
        stokKod: 'Stok',
        prefix: 'Ön ek / firma',
        unit: 't',
      },
    },
    unknown: 'bilinmiyor',
  },

  system: {
    title: 'Sistem Durumu',
    description: 'Kantarların bağlantı ve veri senkronu durumu, canlı.',
    lastSync: 'Son senkron',
    kantarSectionTitle: 'Tesisler',
    syncSectionTitle: 'Senkron durumu',
    fields: {
      kantar_id: 'Kantar kimliği',
      kantar_adi: 'Kantar adı',
      son_gorulme: 'Son görülme',
      ajan_surumu: 'Ajan sürümü',
      kac_dakika_once: 'Son görülmeden bu yana',
      baglanti_durumu: 'Bağlantı durumu',
      tablo: 'Tablo',
      son_sync: 'Son senkron',
      son_tam_yukleme: 'Son tam yükleme',
      kac_saat_once: 'Son senkrondan bu yana',
    } as Record<string, string>,
    minutesSuffix: 'dk',
    hoursSuffix: 'sa',
  },

  overview: {
    title: 'Genel bakış',
    description: 'Hesapların ve sistem bağlantılarının özeti.',
    activeUsers: 'Aktif kullanıcı',
    inactiveUsers: (count: number) => `${count} pasif`,
    lockedUsers: 'Kilitli hesap',
    failedLogins: 'Başarısız giriş (24 saat)',
    mcpConnections: 'Açık Claude bağlantısı',
    statusTitle: 'Bağlantı durumu',
    sources: 'Veri kaynakları',
    sourcesSummary: (ok: number, total: number) => `${ok}/${total} sağlıklı`,
    sync: 'Son senkron',
    noSync: 'Kayıt yok',
    syncHint: 'Gecikme eşiği tanımlı değil; yalnız son senkron zamanı gösterilir.',
    watcher: 'Senkron izleyici',
    running: 'Çalışıyor',
    stopped: 'Durdu',
    lastPoll: (time: string) => `Son yoklama: ${time}`,
    lastError: (error: string) => `Son hata: ${error}`,
    cube: 'Cube (rapor sorguları)',
    reachable: 'Erişiliyor',
    unreachable: 'Erişilemiyor',
    checking: 'Kontrol ediliyor…',
    reportCount: (count: number) => `${count} rapor tanımı yüklendi.`,
    sse: 'Canlı güncelleme',
    sseHint: 'Bu tarayıcının sunucuyla anlık güncelleme bağlantısı.',
  },

  sources: {
    title: 'Veri Kaynakları',
    description: 'Kayıtlı kaynaklar, bağlantı sağlığı ve son senkron bilgisi.',
    status: {
      ok: 'Sağlıklı',
      schema_mismatch: 'Şema uyumsuz',
      unreachable: 'Erişilemiyor',
      not_configured: 'Yapılandırılmamış',
    } as Record<string, string>,
    columns: {
      latency_ms: 'Gecikme (ms)',
      checked_at: 'Kontrol zamanı',
      message: 'Not',
    } as Record<string, string>,
    lastError: 'Son hata',
    schemaIssues: 'Şema sorunları',
  },

  users: {
    title: 'Kullanıcılar',
    description: 'Kullanıcı oluşturma, grup atama ve parola sıfırlama.',
    create: 'Kullanıcı oluştur',
    createTitle: 'Yeni kullanıcı',
    editTitle: 'Kullanıcıyı düzenle',
    fullName: 'Ad soyad',
    email: 'E-posta',
    groups: 'Gruplar',
    isActive: 'Etkin',
    createdAt: 'Oluşturulma',
    lastLoginAt: 'Son etkinlik',
    resetPassword: 'Parolayı sıfırla',
    deactivate: 'Pasifleştir',
    activate: 'Etkinleştir',
    tempPasswordTitle: 'Geçici parola',
    tempPasswordHint:
      'Bu parola yalnızca bir kez gösterilir. Kullanıcıya güvenli bir kanaldan iletin.',
    noGroups: 'Grubu yok',
    searchPlaceholder: 'Ad veya e-posta ara…',
    detailTitle: 'Kullanıcı ayrıntısı',
    detailSections: {
      status: 'Durum',
      groups: 'Gruplar',
      scope: 'Veri kapsamı',
      permissions: 'Etkin izinler',
    },
    lastLogin: 'Son giriş',
    neverLoggedIn: 'Hiç giriş yapmadı',
    lockedUntil: 'Kilitli (bitiş)',
    notLocked: 'Kilitli değil',
    failedLogins: 'Başarısız giriş',
    mustChangePassword: 'Parola değişimi bekleniyor',
    superadminAll: 'Superadmin: tüm izinler ve tüm veriler.',
    firmScopes: { hr: 'Firmalar (İK)', finans: 'Firmalar (Finans & Banka)', kantar: 'Kantarlar' },
    scopeAll: 'Tümü',
    scopeNone: 'İzin yok',
    noPermissions: 'Bu kullanıcının hiçbir izni yok.',
    permissionCount: (count: number) => `${count} izin`,
    sessions: {
      title: 'Oturumlar',
      empty: 'Açık oturum yok.',
      unknownDevice: 'Bilinmeyen cihaz',
      lastRefresh: 'Son yenileme',
      expires: 'Bitiş',
      revoke: 'Sonlandır',
      revokeAll: 'Tümünü sonlandır',
      confirmTitle: 'Oturum sonlandırılsın mı?',
      confirmText: 'Bu cihazdaki oturum kapanır; kullanıcı yeniden giriş yapmak zorunda kalır.',
      confirmAllTitle: 'Tüm oturumlar sonlandırılsın mı?',
      confirmAllText:
        'Kullanıcının bütün cihazlardaki oturumları kapanır. Kendi hesabınızsa siz de çıkış yaparsınız.',
    },
    mcp: {
      title: 'Claude bağlantıları',
      empty: 'Açık Claude bağlantısı yok.',
      unknownClient: 'Claude istemcisi',
      lastRefresh: 'Son yenileme',
      revoke: 'Bağlantıyı kes',
      revokeAll: 'Tümünü kes',
      confirmTitle: 'Claude bağlantısı kesilsin mi?',
      confirmText:
        'Bu istemci verilere erişemez; yeniden bağlanmak için kullanıcının onay vermesi gerekir.',
      confirmAllTitle: 'Tüm Claude bağlantıları kesilsin mi?',
      confirmAllText: 'Kullanıcının bütün Claude istemcilerinin erişimi kapanır.',
    },
    unlock: 'Kilidi aç',
    auditLink: 'Bu kullanıcının işlemleri',
    unlockConfirmTitle: 'Hesabın kilidi açılsın mı?',
    unlockConfirmText: 'Kilit kalkar ve başarısız giriş sayacı sıfırlanır.',
    resetPasswordConfirmTitle: 'Parola sıfırlansın mı?',
    resetPasswordConfirmText: (name: string) =>
      `${name} için yeni bir geçici parola üretilir, açık oturumları kapanır ve ilk girişte parolasını değiştirmesi istenir.`,
  },

  groups: {
    title: 'Gruplar',
    description: 'Gruplar ve izin atamaları. Kullanıcı izinlerini yalnızca gruplardan alır.',
    create: 'Grup oluştur',
    createTitle: 'Yeni grup',
    editTitle: 'Grubu düzenle',
    name: 'Grup adı',
    descriptionField: 'Açıklama',
    nameRequired: 'Grup adı boş olamaz.',
    nameTooLong: 'Grup adı en fazla 100 karakter olabilir.',
    descriptionTooLong: 'Açıklama en fazla 500 karakter olabilir.',
    memberCount: 'Üye sayısı',
    permissionMatrix: 'İzin matrisi',
    viewLabel: 'Görünüm',
    viewGroups: 'Grup ayarları',
    matrixPermission: 'İzin',
    matrixOpenGroup: 'Grubun ayarlarını aç',
    matrixGranted: 'izin var',
    matrixNotGranted: 'izin yok',
    permissionCount: 'İzin sayısı',
    deleteConfirm: 'Grup silinsin mi?',
    deleteConfirmText: (name: string) =>
      `${name} grubu silinir; üyeleri bu gruptan gelen izinleri kaybeder.`,
    save: 'Kaydet',
    saved: 'Grup ayarları güncellendi.',
    unsaved: 'Kaydedilmemiş değişiklik var.',
    areas: {
      hr: {
        title: 'İnsan Kaynakları',
        scope: 'Firma ve şube erişimi',
        hint: 'Bu grubun İnsan Kaynakları ekranlarında görebileceği ve talep açabileceği firma ve şubeler. Firma kutusu o firmanın tüm şubelerini açar; yalnız bazı şubeler için firmayı işaretlemeden şubeleri seçin.',
      },
      finans: {
        title: 'Finans & Banka',
        scope: 'Firma erişimi',
        hint: 'Bu grubun banka hareketleri, bakiyeler, çek, kredi kartı ve POS ekranlarında görebileceği firmalar. Banka verisinde firmanın şubesi bulunmadığı için yalnız firma seçilir.',
      },
      kantar: {
        title: 'Kantar',
        scope: 'Firma ve kantar erişimi',
        hint: 'Bu grubun kantar ekranlarında (Canlı Tartım, Kantar Raporu, Mutabakat) görebileceği kantarlar. Kantar firmanın şubesidir: firma kutusu o firmanın tüm kantarlarını açar; yalnız bazı kantarlar için firmayı işaretlemeden kantarları seçin.',
      },
      other: { title: 'Diğer izinler' },
    },
    scopeRule:
      'Hiçbiri seçilmezse tümünü görür. Kullanıcı birden çok grupta ise birleşimini görür. Kapsam yalnız bu bölümün izinleriyle birlikte geçerlidir.',
    areaPermissions: (count: number) => (count === 0 ? 'izin yok' : `${count} izin`),
    areaFirms: (count: number) => (count === 0 ? 'tüm firmalar' : `${count} firma/şube`),
    firmAll: 'Tüm firmalar',
    firmAllBranches: { hr: 'tüm şubeler', kantar: 'tüm kantarlar' },
    firmNoBranches: 'Şube bilgisi yok ya da kaynağa erişilemedi; yalnız firmanın tümü seçilebilir.',
    scopeBadge: (area: string, labels: string) => `${area}: ${labels}`,
  },

  audit: {
    title: 'Denetim Kaydı',
    description: 'Kimlik ve yönetim olayları.',
    columns: {
      occurred_at: 'Zaman',
      actor_email: 'Aktör',
      action: 'Olay',
      target: 'Hedef',
      ip: 'IP',
      user_agent: 'İstemci',
    } as Record<string, string>,
    filters: {
      actor: 'Kişi',
      action: 'Olay',
    },
    detail: {
      title: 'Kayıt ayrıntısı',
      meta: 'Olay ayrıntısı',
      noMeta: 'Ek ayrıntı yok.',
      openUser: 'Kullanıcıyı aç',
    },
    /** Olay kodlarının adı (backend `audit/actions.py`); bilinmeyen kod ham gösterilir. */
    actions: {
      'auth.login_succeeded': 'Giriş yapıldı',
      'auth.login_failed': 'Giriş başarısız',
      'auth.account_locked': 'Hesap kilitlendi',
      'auth.logout': 'Çıkış yapıldı',
      'auth.token_refreshed': 'Oturum yenilendi',
      'auth.refresh_reuse_detected': 'Oturum anahtarı yeniden kullanıldı',
      'auth.password_changed': 'Parola değiştirildi',
      'user.created': 'Kullanıcı oluşturuldu',
      'user.updated': 'Kullanıcı güncellendi',
      'user.password_reset': 'Kullanıcı parolası sıfırlandı',
      'user.deactivated': 'Kullanıcı pasifleştirildi',
      'user.sessions_revoked': 'Kullanıcı oturumları sonlandırıldı',
      'user.mcp_revoked': 'Kullanıcının Claude bağlantısı kesildi',
      'user.unlocked': 'Hesap kilidi açıldı',
      'group.created': 'Grup oluşturuldu',
      'group.updated': 'Grup güncellendi',
      'group.deleted': 'Grup silindi',
      'group.permissions_updated': 'Grup izinleri güncellendi',
      'group.kantars_updated': 'Grup kantar erişimi güncellendi',
      'group.firms_updated': 'Grup firma erişimi güncellendi',
      'report.create': 'Rapor oluşturuldu',
      'report.update': 'Rapor güncellendi',
      'report.restore': 'Rapor sürümü geri yüklendi',
      'report.delete': 'Rapor silindi',
      'document.generate': 'Belge oluşturuldu',
      'document.update': 'Belge güncellendi',
      'document.refresh': 'Belge verisi yenilendi',
      'document.finalize': 'Belge kesinleştirildi',
      'document.delete': 'Belge silindi',
      'document.pdf': 'Belge PDF alındı',
      'reconcile.override_set': 'Mutabakat kaydı elle düzeltildi',
      'reconcile.override_deleted': 'Mutabakat düzeltmesi kaldırıldı',
      'mcp.authorized': 'Claude bağlantısı onaylandı',
      'mcp.tool_called': 'Claude veri sorguladı',
      'hr.action_requested': 'İK kayıt aksiyonu istendi',
      'hr.process_run': 'İK süreci çalıştırıldı',
      'hr.request_created': 'Giriş/çıkış talebi açıldı',
      'hr.request_resubmitted': 'Giriş/çıkış talebi yeniden gönderildi',
      'hr.request_approved': 'Giriş/çıkış talebi onaylandı',
      'hr.request_revision': 'Giriş/çıkış talebi revizyona gönderildi',
      'hr.request_cancelled': 'Giriş/çıkış talebi iptal edildi',
      'hr.request_retried': 'Giriş/çıkış talebinin DİA aksiyonu yeniden denendi',
      'hr.action_cancelled': 'Kuyruktaki İK aksiyonu iptal edildi',
      'hr.request_resolved': "Tutarsız talep DİA'da elle çözüldü olarak kapatıldı",
      'hr.request_withdrawn': 'Talep açan tarafından geri çekildi',
    } as Record<string, string>,
  },

  /** Rapor belgeleri (şablon editörü, belge listesi); docs/REPORT-DOCUMENTS-PLAN.md. */
  documents: {
    list: {
      title: 'Belgeler',
      description: 'Aylık dönem kopyası üretilen raporlar.',
      empty: 'Henüz belge yok',
      emptyHint: 'Yeni belge ile boş bir rapor ya da pilot örnek oluşturabilirsiniz.',
      emptyReadOnlyHint: 'Size açılmış bir rapor yok.',
      newDocument: 'Yeni belge',
      startBlank: 'Boş rapor',
      startPilot: 'Aylık Banka Raporu örneğinden başla',
      blankTitle: 'Yeni belge',
      blankParagraph: 'Metni buraya yazın.',
      columns: {
        title: 'Belge',
        visibility: 'Paylaşım',
        updated: 'Son güncelleme',
        version: 'Sürüm',
      },
      visibility: { personal: 'Kişisel', groups: 'Gruplar', org: 'Tüm kuruluş' },
      edit: 'Düzenle',
      readOnly: 'Salt okunur',
      createFailed: 'Belge oluşturulamadı.',
    },
    nodes: {
      loading: '…',
      error: '!',
      forbidden: 'yetkiniz yok',
      pagebreak: 'Sayfa sonu',
      note: { info: 'Bilgi', warning: 'Uyarı' },
      condition: {
        then: 'Koşul doğruysa',
        else: 'Değilse',
        header: (left: string, op: string, right: string) => `Koşul: ${left} ${op} ${right} →`,
        ops: { gt: '>', gte: '≥', lt: '<', lte: '≤', eq: '=' },
        activeThen: 'Aktif dal: koşul doğruysa',
        activeElse: 'Aktif dal: değilse',
      },
      repeat: {
        header: (over: string, limit: number) =>
          `Her satır için tekrarlanır: ${over} (en fazla ${limit} satır; raporda bir kez, ilk satırla gösterilir)`,
        rows: (count: number) => `· ${count} satır var`,
      },
    },
    editor: {
      back: 'Belgelere dön',
      untitled: 'Başlıksız belge',
      save: 'Kaydet',
      saving: 'Kaydediliyor…',
      saved: (version: number) => `Kaydedildi (sürüm ${version}).`,
      unsaved: 'Kaydedilmemiş değişiklikler var',
      conflict: 'Raporu başkası değiştirdi. Yeniden yükleyip değişikliklerinizi tekrar uygulayın.',
      reload: 'Yeniden yükle',
      invalidTitle: 'Rapor şemaya uymuyor, kaydedilmedi:',
      invalidDefinition: 'Kayıtlı rapor tanımı geçersiz, editörde açılamıyor:',
      readOnly: 'Bu raporu düzenleme yetkiniz yok; salt okunur görüntüleniyor.',
      forbiddenView: 'Bu görünüm için yetkiniz yok.',
      memberMissing: (member: string) => `${member} görünümde yok.`,
      pageLabel: 'Belge sayfası',
      previewPeriod: 'Dönem',
      previewFilters: 'Önizleme verisi için dönem ve filtreler',
      toolbar: {
        label: 'Biçimlendirme',
        paragraph: 'Paragraf',
        heading: (level: number) => `Başlık ${level}`,
        bold: 'Kalın',
        italic: 'İtalik',
        bulletList: 'Madde işaretli liste',
        orderedList: 'Numaralı liste',
        table: 'Metin tablosu ekle',
        link: 'Bağlantı',
        undo: 'Geri al',
        redo: 'Yinele',
      },
      link: {
        placeholder: 'https://… ya da /yol',
        apply: 'Uygula',
        remove: 'Bağlantıyı kaldır',
        invalid: 'Bağlantı yalnız göreli ya da https:// olabilir.',
      },
      insert: {
        menu: 'Ekle',
        kpi: 'KPI',
        table: 'Tablo',
        list: 'Liste',
        chart: 'Grafik',
        value: 'Değer',
        change: 'Değişim',
        condition: 'Koşul',
        repeat: 'Tekrar',
        pagebreak: 'Sayfa sonu',
        note: 'Not kutusu',
        needMeta: 'Görünüm listesi yüklenmedi ya da hiçbir görünüme yetkiniz yok.',
        needMember: 'Seçilen görünümde bu blok için uygun üye yok.',
        needQuery: 'Önce Rapor sekmesinde bir sorgu tanımlayın.',
        needCompare: 'Önceki dönemle karşılaştırmalı bir sorgu gerekir.',
        needRepeatQuery: 'Boyutlu (çok satırlı) bir sorgu gerekir.',
        nestedRepeat: 'Tekrar bloğunun içine tekrar eklenemez.',
      },
      tabs: { block: 'Blok', template: 'Rapor', versions: 'Sürümler' },
      block: {
        none: 'Ayarlarını görmek için bir blok seçin: widget, değer, değişim, koşul, tekrar ya da not.',
        titles: {
          reportKpi: 'KPI',
          reportTable: 'Tablo',
          reportList: 'Liste',
          reportChart: 'Grafik',
          value: 'Değer',
          change: 'Değişim',
          condition: 'Koşul',
          repeat: 'Tekrar',
          note: 'Not kutusu',
          pagebreak: 'Sayfa sonu',
        } as Record<string, string>,
        blockTitle: 'Başlık',
        measure: 'Ölçü',
        measures: 'Ölçüler',
        dimensions: 'Boyutlar',
        columns: 'Sütunlar',
        rowKey: 'Satır anahtarı',
        time: 'Zaman boyutu',
        granularity: 'Zaman kırılımı',
        granularities: {
          day: 'Gün',
          week: 'Hafta',
          month: 'Ay',
          quarter: 'Çeyrek',
          year: 'Yıl',
        } as Record<string, string>,
        series: 'Seri',
        seriesType: (measure: string) => `${measure} seri türü`,
        seriesBar: 'Sütun',
        seriesLine: 'Çizgi',
        removeSeries: (measure: string) => `${measure} serisini kaldır`,
        range: 'Aralık (ör. -12m)',
        rangeHint: 'Seçili dönemde biten son aralık; boş bırakılırsa dönem filtresi uygulanır.',
        rangeInvalid: 'Biçim: -12m, -1y ya da -30d.',
        horizontal: 'Yatay çubuk',
        totals: 'Toplam satırı',
        totalsMeasures: 'Toplamı alınacak ölçüler',
        limit: 'Satır sınırı',
        order: 'Sıralama',
        defaultSort: 'Varsayılan sıralama',
        where: 'Süzgeç (eşitlik)',
        ignoreFilters: 'Uygulanmayacak filtreler',
        of: 'Değer kaynağı',
        left: 'Sol taraf',
        op: 'Karşılaştırma',
        right: 'Sağ taraf',
        rightKind: 'Sağ taraf türü',
        rightNumber: 'Sayı',
        rightRef: 'Başvuru',
        ops: {
          gt: 'büyük (>)',
          gte: 'büyük eşit (≥)',
          lt: 'küçük (<)',
          lte: 'küçük eşit (≤)',
          eq: 'eşit (=)',
        },
        over: 'Tekrarlanan sorgu',
        noteType: 'Not türü',
        noOptions: 'Seçenek yok',
        pickPlaceholder: 'Seçin…',
      },
      fields: {
        add: 'Ekle',
        remove: (label: string) => `${label} kaldır`,
        direction: 'Yön',
        asc: 'artan',
        desc: 'azalan',
        member: 'Üye',
        whereMember: 'Süzgeç üyesi',
        whereValues: 'Değerler (virgülle ayırın)',
        none: 'Seçilmedi',
      },
      refs: {
        period: 'Dönem',
        filters: 'Filtreler',
        row: 'Satır',
        periodLabel: 'Dönem adı',
        filter: (member: string) => `Seçili ${member}`,
        previous: (name: string) => `Önceki dönem · ${name}`,
      },
      template: {
        title: 'Belge başlığı',
        orientation: 'Sayfa yönü',
        portrait: 'Dikey',
        landscape: 'Yatay',
        visibility: 'Paylaşım',
        visibilityKinds: { personal: 'Kişisel', groups: 'Gruplar', org: 'Tüm kuruluş' },
        filters: 'Filtreler',
        noFilters: 'Tanımlı filtre yok.',
        addPeriod: 'Dönem filtresi',
        addMultiSelect: 'Çoklu seçim filtresi',
        filterView: 'Görünüm',
        filterMember: 'Üye',
        periodDefault: 'Varsayılan dönem',
        periodDefaults: {
          '-0m': 'Bu ay',
          '-1m': 'Geçen ay',
          '-2m': '2 ay önce',
          '-3m': '3 ay önce',
          '-1y': '12 ay önce',
        } as Record<string, string>,
        periodOnlyOne: 'En fazla bir dönem filtresi olabilir.',
        filterKinds: {
          period: 'Dönem',
          multiSelect: 'Çoklu seçim',
          dateRange: 'Tarih aralığı',
          boolean: 'Evet/Hayır',
        } as Record<string, string>,
        removeFilter: (label: string) => `${label} filtresini kaldır`,
        queries: 'Adlandırılmış sorgular',
        noQueries: 'Henüz sorgu yok. Değer, değişim, koşul ve tekrar bu sorgulara başvurur.',
        addQuery: 'Sorgu ekle',
        queryName: 'Sorgu adı',
        nameInvalid:
          'Ad harfle başlamalı; harf, rakam ve _ içerebilir. row, prev, period, filters ayrılmıştır.',
        nameTaken: 'Bu ad kullanılıyor.',
        create: 'Oluştur',
        compare: 'Önceki dönemle karşılaştır',
        usedBy: (count: number) => `${count} yerde kullanılıyor`,
        deleteQuery: (name: string) => `${name} sorgusunu sil`,
        deleteBlocked: 'Kullanıldığı yerler silinmeden sorgu silinemez.',
        editQuery: (name: string) => `${name} sorgusunu düzenle`,
        closeQuery: 'Kapat',
      },
      versions: {
        title: 'Sürüm geçmişi',
        loading: 'Sürümler yükleniyor…',
        empty: 'Sürüm yok.',
        current: 'güncel',
        label: (version: number) => `Sürüm ${version}`,
        restore: 'Bu sürüme dön',
        restoreConfirm: (version: number) =>
          `Sürüm ${version} içeriği yeni bir sürüm olarak geri yüklenecek. Kaydedilmemiş değişiklikler kaybolur. Devam edilsin mi?`,
        restored: (version: number) => `Sürüm ${version} geri yüklendi.`,
        readOnly: 'Sürüm geçmişini yalnız düzenleyenler görür.',
      },
    },
    runs: {
      readOnly: 'Bu kopya salt okunur: rakamlar kopya üretilirken kaydedilen veridir.',
      status: { draft: 'Taslak', final: 'Kesin' },
      dataAt: (when: string) => `Veri: ${when}`,
      dataMissing: (key: string) => `Kopyada ${key} verisi yok.`,
      edit: {
        hint: 'Rakamlar kopya üretilirken kaydedilen veridir; yalnız metin düzenlenir.',
        final: 'Kesin kopya düzenlenemez.',
        noPermission: 'Bu kopyayı düzenleme yetkiniz yok.',
        conflict:
          'Kopyayı başkası değiştirdi. Yeniden yükleyip değişikliklerinizi tekrar uygulayın.',
        block: {
          group: 'Blok',
          remove: 'Bloğu sil',
          moveUp: 'Bloğu yukarı taşı',
          moveDown: 'Bloğu aşağı taşı',
        },
      },
      refresh: {
        action: 'Verileri yenile',
        running: 'Yenileniyor…',
        confirmFinal:
          'Bu kopya kesin. Verileri yenilemek kopyayı yeniden taslağa alır. Devam edilsin mi?',
        done: (changed: number) => `Veriler yenilendi; değişen rakam sayısı: ${changed}.`,
        textKept: 'Metin değişmedi; aşağıdaki bloklar yeni veriyle yeniden gözden geçirilmeli:',
        condition: (n: number) => `Koşul ${n}`,
        repeat: (n: number) => `Tekrar ${n}`,
        failed: 'Veriler yenilenemedi.',
        invalidRun: 'Kopya içeriği okunamadı; veriler yenilenemedi.',
      },
      finalize: {
        action: 'Kesinleştir',
        running: 'Kesinleştiriliyor…',
        unsaved: 'Önce değişiklikleri kaydedin.',
        failed: 'Kopya kesinleştirilemedi.',
      },
      regenerate: {
        action: 'Rapordan yeniden üret',
        title: 'Filtreleri yeniden seçin',
        submit: 'Yeniden üret',
        running: 'Üretiliyor…',
        cancel: 'Vazgeç',
        confirm:
          'Kopyanın metin düzenlemeleri atılır ve kopya rapordan yeniden üretilir. Devam edilsin mi?',
        failed: 'Kopya yeniden üretilemedi.',
      },
      remove: {
        action: 'Sil',
        running: 'Siliniyor…',
        confirm: 'Bu taslak kopya kalıcı olarak silinecek. Devam edilsin mi?',
        failed: 'Kopya silinemedi.',
      },
      generate: {
        title: 'Kopyalar',
        description: 'Rapordan dönem başına üretilen dondurulmuş kopyalar.',
        newRun: 'Yeni dönem üret',
        submit: 'Üret',
        submitting: 'Üretiliyor…',
        cancel: 'Vazgeç',
        period: 'Dönem',
        all: 'Tümü',
        columns: { period: 'Dönem', status: 'Durum', createdBy: 'Üreten', createdAt: 'Tarih' },
        empty: 'Henüz kopya yok',
        emptyHint: 'Yeni dönem üret ile bir dönemin kopyasını oluşturun.',
        open: 'Aç',
        copies: 'Kopyalar',
        forbidden: (view: string) => `${view || 'Bu veri'} için yetkiniz yok; kopya üretilemedi.`,
        failed: 'Kopya üretilemedi.',
      },
      pdf: {
        action: 'PDF önizle',
        busy: 'Hazırlanıyor…',
        download: 'İndir',
        frameTitle: 'PDF önizleme',
        errors: {
          pdf_renderer_unavailable: 'PDF servisine ulaşılamadı; biraz sonra tekrar deneyin.',
          pdf_render_timeout: 'PDF zamanında hazırlanamadı; tekrar deneyin.',
          pdf_render_failed: 'PDF hazırlanamadı.',
        } as Record<string, string>,
        failed: 'PDF hazırlanamadı.',
      },
      print: {
        action: 'Yazdır',
        back: 'Kopyaya dön',
        letterhead: 'Betontaş Grup Rapor',
        watermark: 'TASLAK',
        period: (label: string) => `Dönem: ${label}`,
        generatedAt: (when: string) => `Üretim: ${when}`,
        refreshedAt: (when: string) => `Yenileme: ${when}`,
        page: 'Sayfa',
        listTruncated: (limit: number, total: number) =>
          `İlk ${limit} satır gösteriliyor (toplam ${total}); tamamı Excel'de.`,
      },
    },
  },

  /** İnsan Kaynakları paneli (docs/IK-PANEL-PLAN.md). */
  hr: {
    unknown: 'Bilinmiyor',
    system: 'Sistem',
    yes: 'Evet',
    no: 'Hayır',
    filters: {
      firm: 'Firma',
      branch: 'Şube',
      status: 'Durum',
      category: 'Hata kategorisi',
      leaveType: 'İzin türü',
      dates: 'Tarih',
      search: 'Ad ya da sicil',
      searchPlaceholder: 'Ad soyad ya da sicil no…',
      diaStatus: 'DİA durumu',
      passgageStatus: 'Passgage durumu',
      source: 'Kaynak',
    },
    /** Hareketin kaynağı (Cube `IkHareket.kaynak`). */
    source: { passgage: 'Passgage', panel: 'Panelden eklenen' } as Record<string, string>,
    girisCikis: { check_in: 'Giriş', check_out: 'Çıkış', G: 'Giriş', Ç: 'Çıkış' } as Record<
      string,
      string
    >,
    /** Hareket ve izin durumları (Cube `durum`, spec §3.1/§3.2). */
    recordStatus: {
      bekliyor: 'Bekliyor',
      hatali: 'Hatalı',
      aktarildi: 'DİA’ya aktarıldı',
      silindi: 'Passgage’de silindi',
      yoksayildi: 'Yoksayıldı',
      elle_girildi: 'DİA’ya elle girildi',
      atlandi: 'Atlandı (milat öncesi)',
    } as Record<string, string>,
    passgagePush: {
      aktarildi: 'Passgage’e gönderildi',
      hatali: 'Passgage hatası',
      bekliyor: 'Passgage’e gidecek',
    } as Record<string, string>,
    diaStatus: { A: 'DİA aktif', P: 'DİA pasif' } as Record<string, string>,
    passgageActive: { true: 'Passgage aktif', false: 'Passgage pasif' } as Record<string, string>,
    category: {
      firma_eslesmesi: 'Firma eşleşmesi yok',
      donem_kodu: 'Dönem kodu yok',
      gecersiz_tip: 'Geçersiz tip',
      izin_cakismasi: 'İzin çakışması',
      oturum: 'DİA oturumu',
      dia_hatasi: 'DİA/ağ hatası',
      personel_yok: 'Personel bulunamadı',
      firma_config: 'Firma tanımı yok',
      izin_turu_eslesmesi: 'İzin türü eşleşmesi yok',
      sure_hesaplanamadi: 'Süre hesaplanamadı',
      tarih_hatali: 'Tarih hatalı',
      firma_belirsiz: 'Firma belirsiz',
    } as Record<string, string>,
    /** Panel aksiyon kuyruğu durumları (backend `HrAction.status`). */
    actionStatus: {
      bekliyor: 'Sırada',
      calisiyor: 'Çalışıyor',
      tamam: 'Tamamlandı',
      hata: 'Başarısız',
      tutarsiz: 'Tutarsız',
      iptal: 'İptal edildi',
      istendi: 'İstendi',
      bilgi: 'Bilgi',
    } as Record<string, string>,
    actions: {
      menu: 'Aksiyonlar',
      label: {
        hareket_tekrar_gonder: 'DİA’ya tekrar gönder',
        hareket_passgage_guncel_al: 'Passgage’den güncelini al',
        hareket_yoksay: 'Hareketi yoksay',
        hareket_elle_girildi: '“DİA’ya elle girildi” işaretle',
        hareket_dia_sil: 'DİA’dan sil',
        hareket_dia_kalsin: 'DİA’da kalsın',
        izin_tekrar_gonder: 'DİA’ya tekrar gönder',
        izin_passgage_guncel_al: 'Passgage’den güncelini al',
        izin_yoksay: 'İzni yoksay',
        izin_saat_duzelt: 'İzin saatini düzelt',
        izin_duzeltme_onar: 'Passgage’i DİA ile hizala',
        karar_kaldir: 'Panel kararını kaldır',
        personel_dia_guncel_al: 'DİA’dan güncelini al',
        personel_passgage_tekrar_gonder: 'Passgage’e tekrar gönder',
        personel_pasife_al: 'Pasife al (DİA + Passgage)',
        personel_pasife_al_tamamla: 'Pasife almayı tamamla',
        talep_giris_cikis_ekle: 'DİA’ya giriş/çıkış ekle',
        talep_giris_cikis_duzenle: 'DİA’daki giriş/çıkışı düzenle',
        talep_giris_cikis_sil: 'DİA’dan giriş/çıkışı sil',
        talep_izin_guncelle: 'Passgage’de izni güncelle',
        talep_izin_iptal: 'Passgage’de izni iptal et',
        surec_calistir: 'Süreci çalıştır',
      } as Record<string, string>,
      progress: {
        queued: (label: string) => `${label}: sıraya alındı`,
        queuedHint: 'Akış işi aldığında başlayacak; bu bildirim durumu izler.',
        running: (label: string) => `${label}: devam ediyor`,
        runningHint: 'Akış işliyor…',
        done: {
          tamam: (label: string) => `${label}: tamamlandı`,
          hata: (label: string) => `${label}: başarısız`,
          tutarsiz: (label: string) => `${label}: tutarsız kaldı`,
          iptal: (label: string) => `${label}: kuyruktan çıkarıldı`,
        } as Record<string, (label: string) => string>,
        timeout: 'Durum hâlâ açık; satırda ve geçmişte izlenebilir.',
      },
      failed: 'Aksiyon başlatılamadı',
      cancelQueued: 'Kuyruktan çıkar',
      cancelQueuedFailed: 'Aksiyon kuyruktan çıkarılamadı',
      reason: 'Açıklama',
      reasonPlaceholder: 'Neden? (geçmişte görünür)',
      confirm: 'Onayla',
      cancel: 'Vazgeç',
      deactivateTitle: 'Personel pasife alınsın mı?',
      deactivateBody:
        'Önce DİA’da kart pasife alınır, sonra Passgage’de kullanıcı pasifleşir. DİA adımı geri alınmaz; Passgage adımı başarısız olursa satırda “Tutarsız” görünür ve tamamlanabilir.',
      inconsistentHint: 'DİA’da pasif, Passgage adımı tamamlanmadı.',
      complete: 'Tamamla',
    },
    hourFix: {
      title: 'İzin saatini düzelt',
      body: 'Yalnız aynı gün içindeki saatlik izin. DİA ve Passgage birlikte güncellenir, bağlı hareket yeniden gönderilir.',
      start: 'Başlangıç saati',
      end: 'Bitiş saati',
      invalid: 'Başlangıç bitişten önce olmalı.',
      notHourly: 'Tam gün / çok günlü izin “İzni güncelle” talebiyle değiştirilir.',
    },
    detail: {
      history: 'Geçmiş',
      historyEmpty: 'Bu kayıt için geçmiş yok.',
      historyPartial: 'Sistem olaylarına ulaşılamadı; yalnız panel olayları gösteriliyor.',
      error: 'Hata',
      attempts: 'Deneme',
      lastAttempt: 'Son deneme',
      diaKey: 'DİA anahtarı',
      decision: 'Panel kararı',
      decisionBy: (who: string) => `Kararı veren: ${who}`,
      coveringLeave: 'Kapsayan izin',
      openLeave: 'İzni aç',
      newEditRequest: 'Düzenleme talebi aç',
      fields: {
        sicil: 'Sicil no',
        firma: 'Firma',
        sube: 'Şube',
        pozisyon: 'Pozisyon',
        iseBaslama: 'İşe başlama',
        passgageId: 'Passgage kullanıcı',
        zaman: 'Zaman',
        tip: 'Tip',
        izinTuru: 'İzin türü',
        baslangic: 'Başlangıç',
        bitis: 'Bitiş',
        saatlik: 'Saatlik',
        sure: 'Süre',
        aciklama: 'Açıklama',
        diaIzinKey: 'DİA izin anahtarı',
        passgageDurum: 'Passgage durumu',
      },
      durationMinutes: (minutes: number) => `${minutes} dk`,
    },
    history: {
      eklendi: 'Kayıt oluştu',
      guncellendi: 'Kayıt güncellendi',
      silindi: 'Kayıt silindi',
      field: {
        status: 'Durum',
        api_status: 'DİA aktarımı',
        dia_izin_key: 'DİA izin anahtarı',
        start_date: 'Başlangıç',
        end_date: 'Bitiş',
        hata_kategori: 'Hata kategorisi',
        son_hata: 'Hata',
        dia_key: 'DİA anahtarı',
        entrance_type: 'Tip',
        created_at: 'Zaman',
        durum: 'DİA durumu',
        passgage_id: 'Passgage kullanıcı',
        sube: 'Şube',
        __dk__8: 'Firma',
        karar: 'Panel kararı',
        aciklama: 'Açıklama',
        kullanici: 'Kullanıcı',
        sonuc: 'Sonuç',
        giris_cikis: 'Tip',
        occurred_at: 'Zaman',
        description: 'Açıklama',
        tur: 'İzin türü',
        saatlik: 'Saatlik',
        baslangic: 'Başlangıç',
        bitis: 'Bitiş',
      } as Record<string, string>,
      empty: '(boş)',
      talep: {
        olusturuldu: 'Talep açıldı',
        revizyon_istendi: 'Revizyona gönderildi',
        yeniden_gonderildi: 'Düzeltilip yeniden gönderildi',
        yeniden_denendi: 'DİA aksiyonu yeniden denendi',
        onaylandi: 'Onaylandı',
        iptal_edildi: 'İptal edildi',
        elle_cozuldu: 'DİA’da elle çözüldü',
        geri_cekildi: 'Talebi açan geri çekti',
      } as Record<string, string>,
      step: (step: string) => `Adım: ${step}`,
    },
    personnel: {
      title: 'Personel',
      description:
        'Bütün firmaların DİA personel kartları ve Passgage’e gönderim durumu. Satırı açınca ayrıntı, geçmiş ve aksiyonlar görünür.',
      total: 'Personel',
      active: 'Aktif',
      passive: 'Pasif',
      passgageErrors: 'Passgage hatalı',
      branches: 'Şube kırılımı',
      columns: {
        ad: 'Ad soyad',
        sicil: 'Sicil',
        firma: 'Firma',
        sube: 'Şube',
        pozisyon: 'Pozisyon',
        dia: 'DİA',
        passgage: 'Passgage',
        hata: 'Son hata',
        aksiyon: 'Son aksiyon',
      },
    },
    attendance: {
      title: 'Giriş/Çıkış',
      description:
        'Passgage giriş/çıkış hareketleri ve DİA’ya aktarım durumu. Hatalılar her çalışmada yeniden denenir; kalıcı hatalar için aksiyon gerekir.',
      total: 'Hareket',
      errors: 'Hatalı',
      panelBadge: 'Panelden eklendi',
      panelHint:
        'Bu hareket panelden onaylı talep ile DİA’ya eklendi; Passgage’de yok. Düzenleme ve silme de talep ile yapılır.',
      edit: 'Düzenle',
      delete: 'DİA’dan sil',
      columns: {
        ad: 'Ad soyad',
        sicil: 'Sicil',
        tip: 'Tip',
        zaman: 'Zaman',
        firma: 'Firma',
        sube: 'Şube',
        durum: 'Durum',
        kategori: 'Hata kategorisi',
        deneme: 'Deneme',
        aksiyon: 'Son aksiyon',
      },
    },
    leaves: {
      title: 'İzinler',
      description:
        'Passgage’de onaylanmış izinler ve DİA’ya aktarım durumu; otomatik saat düzeltmeleri. İzin çakışmaları İşlerim’de.',
      tabs: { list: 'İzinler', conflicts: 'Çakışmalar', fixes: 'Otomatik düzeltmeler' },
      columns: {
        ad: 'Ad soyad',
        sicil: 'Sicil',
        tur: 'İzin türü',
        baslangic: 'Başlangıç',
        bitis: 'Bitiş',
        saatlik: 'Saatlik',
        sure: 'Süre',
        durum: 'Durum',
        hata: 'Hata',
        aksiyon: 'Son aksiyon',
      },
      conflicts: {
        description:
          'DİA, personelin o anı kapsayan bir izni varken giriş/çıkışı reddeder. Hareket ve kapsayan izin yan yana: saatlik izni düzeltin ya da hareketi yoksayın.',
        movement: 'Hareket',
        leave: 'Kapsayan izin',
        noLeave: 'Kapsayan onaylı izin bulunamadı',
        empty: 'Çakışan hareket yok.',
      },
      fixes: {
        description:
          'Sistemin otomatik kısalttığı saatlik izinler. “Tutarsız”: DİA güncellendi, Passgage güncellenemedi.',
        columns: {
          zaman: 'Zaman',
          ad: 'Ad soyad',
          tur: 'İzin türü',
          eski: 'Eski aralık',
          yeni: 'Yeni aralık',
          sonuc: 'Sonuç',
          hata: 'Hata',
        },
        result: { basarili: 'Başarılı', tutarsiz: 'Tutarsız', hata: 'Hata' } as Record<
          string,
          string
        >,
        onlyOpen: 'Yalnız aksiyon bekleyenler',
      },
    },
    processes: {
      title: 'Süreçler',
      description:
        'Prefect’te çalışan Passgage ↔ DİA süreçleri. Mevcut akışlar aynen çalışır; panel yalnız izler ve “şimdi çalıştır” ile tetikler.',
      names: {
        giris_cikis: 'Giriş/çıkış aktarımı',
        izin: 'İzin senkronu',
        personel: 'Personel kartı senkronu',
        duzeltme_raporu: 'Haftalık izin düzeltme raporu',
        aksiyon: 'İK panel aksiyonları',
      } as Record<string, string>,
      lastRun: 'Son çalışma',
      nextRun: 'Sıradaki',
      running: 'Şu an çalışıyor',
      duration: 'Süre',
      notScheduled: 'Planlı değil',
      paused: 'Duraklatıldı',
      never: 'Hiç çalışmadı',
      notFound: 'Prefect’te bulunamadı',
      notConfigured: 'Prefect bağlantısı yapılandırılmamış.',
      unreachable: 'Prefect’e ulaşılamıyor',
      unreachableBody:
        'Süreç durumları şu an okunamıyor. Passgage sağlığı ve aksiyon kuyruğu yine de güncel; akışlar Prefect düzelince kaldığı yerden çalışır.',
      runConfirmTitle: (name: string) => `${name} şimdi çalıştırılsın mı?`,
      runConfirmBody:
        'Süreç planlı zamanını beklemeden Prefect’te başlatılır. Çalışan bir örnek varsa ikincisi başlatılmaz.',
      notConfiguredCard: 'Prefect yapılandırılmamış',
      failedBadge: 'Son çalışma başarısız',
      overdueBadge: 'Planlı zamanı geçti',
      okBadge: 'Sağlıklı',
      runNow: 'Şimdi çalıştır',
      runStarted: (name: string) => `${name} başlatıldı`,
      runFailed: 'Süreç başlatılamadı',
      openPrefect: 'Prefect’te aç',
      runs: 'Çalışmalar',
      checkedAt: (time: string) => `Son kontrol ${time}`,
      health: 'Passgage API sağlığı',
      healthHint: 'Üst üste “0 kayıt” çekilen çalışma sayısı (2 ve üstü uyarı).',
      healthZero: (count: number) => `${count} çalışmadır 0 kayıt`,
      healthUnavailable: 'Passgage veritabanına ulaşılamadı.',
      queue: 'Aksiyon kuyruğu',
      queueHint: 'Akışın henüz almadığı panel aksiyonları.',
      queueDepth: (count: number) => (count === 0 ? 'Kuyruk boş' : `${count} aksiyon bekliyor`),
      queueOldest: (since: string) => `En eskisi ${since} bekliyor`,
      runColumns: {
        name: 'Run',
        state: 'Sonuç',
        start: 'Başlangıç',
        duration: 'Süre',
        link: 'Prefect',
      },
      state: {
        COMPLETED: 'Tamamlandı',
        FAILED: 'Başarısız',
        CRASHED: 'Çöktü',
        CANCELLED: 'İptal',
        CANCELLING: 'İptal ediliyor',
        RUNNING: 'Çalışıyor',
        PENDING: 'Bekliyor',
        SCHEDULED: 'Planlı',
        PAUSED: 'Duraklatıldı',
      } as Record<string, string>,
      seconds: (value: number) =>
        value < 60
          ? `${Math.round(value)} sn`
          : `${Math.floor(value / 60)} dk ${Math.round(value % 60)} sn`,
    },
    approvals: {
      title: 'Onaylar',
      description:
        'Elle giriş/çıkış ekleme ve düzenleme talepleri ile izin güncelleme talepleri. İK onaylar, revizyona yollar ya da iptal eder; onaylanan giriş/çıkış DİA’ya, izin Passgage’e gider.',
      tabs: {
        onay_bekliyor: 'Bekleyen',
        revizyonda: 'Revizyonda',
        onaylandi: 'Onaylı',
        iptal: 'İptal',
      } as Record<string, string>,
      kind: {
        giris_cikis_ekle: 'Ekleme',
        giris_cikis_duzenle: 'Düzenleme',
        giris_cikis_sil: 'Silme',
        izin_guncelle: 'İzin güncelleme',
        izin_iptal: 'İzin iptali',
      } as Record<string, string>,
      columns: {
        personel: 'Personel',
        firma: 'Firma',
        tur: 'Tür',
        tip: 'Tip',
        zaman: 'Zaman',
        acan: 'Açan',
        guncelleme: 'Güncelleme',
        dia: 'Gönderim',
        bekliyor: 'Bekliyor',
      },
      newRequest: 'Yeni talep',
      newTitle: 'Elle giriş/çıkış talebi',
      editTitle: 'Giriş/çıkış düzenleme talebi',
      person: 'Personel',
      personPlaceholder: 'Ad ya da sicil ile arayın…',
      personEmpty: 'Eşleşen personel yok.',
      type: 'Tip',
      date: 'Tarih',
      time: 'Saat',
      descriptionLabel: 'Açıklama',
      descriptionPlaceholder: 'Neden elle giriliyor?',
      submit: 'Onaya gönder',
      submitDirect: 'Kaydet ve DİA’ya gönder',
      created: 'Talep onaya gönderildi',
      direct: 'Onaysız işle',
      directHint:
        'Talep onaya düşmeden onaylanır ve DİA’ya gönderilir; geçmişte sizin onayınız olarak görünür.',
      approve: 'Onayla',
      approved: 'Talep onaylandı; DİA’ya gönderiliyor',
      revise: 'Revizyona yolla',
      revised: 'Talep revizyona gönderildi',
      cancel: 'Talebi iptal et',
      cancelled: 'Talep iptal edildi',
      cancelReason: 'İptal gerekçesi',
      cancelReasonPlaceholder: 'Neden iptal ediliyor? Talebi açana iletilir.',
      withdraw: 'Talebi geri çek',
      withdrawn: 'Talep geri çekildi',
      withdrawHint: 'Talep henüz karara bağlanmadı; gerekçeyle geri çekebilirsiniz.',
      withdrawReason: 'Geri çekme gerekçesi',
      withdrawPlaceholder: 'Neden geri çekiliyor?',
      approveConfirmTitle: 'Talep onaylansın mı?',
      directConfirmTitle: 'Talep onaysız işlensin mi?',
      approveConfirmBody: 'Onaydan sonra DİA’ya gönderilir; bu adım geri alınmaz.',
      approveDisabled: {
        scope: 'Bu talebin firması ya da şubesi onay kapsamınızda değil.',
        status: 'Talep onay beklemiyor.',
      },
      revisionReason: 'Revizyon nedeni',
      revisionBy: (who: string, when: string) => `${who} · ${when}`,
      checkWarnings: 'Dikkat',
      timeInvalid: 'Saat SS:DD biçiminde olmalı (ör. 08:00).',
      descriptionRequired: 'Açıklama yazın.',
      futureTime: 'Gelecekteki bir saat seçilemez.',
      cancelConfirm: 'Talep iptal edilsin mi? DİA’ya hiçbir şey gönderilmez.',
      retry: 'DİA’ya yeniden gönder',
      retried: 'Aksiyon yeniden kuyruğa alındı',
      resolve: 'DİA’da elle çözüldü',
      resolved: 'Talep elle çözüldü olarak kapatıldı',
      resolveHint:
        'Akış sonuç bildiremedi; hedef sistemde yazılmış olabilir. Kontrol edip elle düzelttiyseniz gerekçeyle kapatın. Yeniden gönderim çift kayıt yaratabileceği için kapalıdır.',
      resolvePlaceholder: 'Ne kontrol edildi, ne yapıldı?',
      firm: 'Firma',
      firmHint: 'Personelin birden çok firmada aktif kartı var; talebin gideceği firmayı seçin.',
      retryHint:
        'DİA’ya gönderilemedi. Sorunu giderdiyseniz aynı talebi yeniden kuyruğa alabilirsiniz.',
      resubmit: 'Düzeltip yeniden gönder',
      resubmitted: 'Talep yeniden onaya gönderildi',
      message: 'Mesaj',
      messagePlaceholder: 'Açıklama yazın…',
      messageRequired: 'Revizyon için açıklama gerekli.',
      thread: 'Yazışma',
      diaPending: 'Onay bekliyor; DİA’ya henüz bir şey gitmedi.',
      diaCancelled: 'İptal edildi; DİA’ya bir şey gönderilmedi.',
      diaRevision: 'Revizyonda; DİA’ya henüz bir şey gitmedi.',
      diaStatus: 'DİA durumu',
      targetsNote: 'Hedef: yalnız DİA. Elle eklenen giriş/çıkış Passgage’de görünmez.',
      leave: {
        title: 'İzin güncelleme talebi',
        open: 'İzni güncelle',
        targetsNote:
          'Hedef: Passgage. Onaydan sonra izin Passgage’de güncellenir; DİA’yı izin süreci aynı kayıt üzerinden günceller (mesai saatlerinde 5 dakikada bir).',
        current: 'Mevcut',
        requested: 'İstenen',
        type: 'İzin türü',
        typeHint: 'Yalnız DİA’da karşılığı olan türler listelenir.',
        hourly: 'Saatlik izin',
        start: 'Başlangıç',
        end: 'Bitiş',
        startTime: 'Başlangıç saati',
        endTime: 'Bitiş saati',
        unchanged: 'Yeni değerler mevcut izinle aynı.',
        typeRequired: 'İzin türü seçin.',
        dateRequired: 'Tarih seçin.',
        endBeforeStart: 'Bitiş başlangıçtan önce olamaz.',
        endTimeBeforeStart: 'Bitiş saati başlangıç saatinden sonra olmalı.',
        descriptionPlaceholder: 'Neden güncelleniyor?',
        submitDirect: 'Kaydet ve Passgage’e gönder',
        directHint:
          'Talep onaya düşmeden onaylanır ve Passgage’e gönderilir; geçmişte sizin onayınız olarak görünür.',
        approved: 'Talep onaylandı; Passgage’e gönderiliyor',
        approveConfirmBody: 'Onaydan sonra Passgage’e gönderilir; bu adım geri alınmaz.',
        cancelConfirm: 'Talep iptal edilsin mi? Passgage’e hiçbir şey gönderilmez.',
        retry: 'Passgage’e yeniden gönder',
        retryHint:
          'Passgage’e gönderilemedi. Sorunu giderdiyseniz aynı talebi yeniden kuyruğa alabilirsiniz.',
        diaPending: 'Onay bekliyor; Passgage’e henüz bir şey gitmedi.',
        diaCancelled: 'İptal edildi; Passgage’e bir şey gönderilmedi.',
        diaRevision: 'Revizyonda; Passgage’e henüz bir şey gitmedi.',
        diaStatus: 'Passgage durumu',
      },
      cancelLeave: {
        title: 'İzin iptal talebi',
        open: 'İzni iptal et',
        targetsNote:
          'Hedef: Passgage. Onaydan sonra izin Passgage’de iptal edilir; DİA’daki kaydı izin süreci siler (mesai saatlerinde 5 dakikada bir).',
        requested: 'İptal',
        descriptionPlaceholder: 'Neden iptal ediliyor?',
        submitDirect: 'İptal et ve Passgage’e gönder',
      },
      original: 'Düzenlenen hareket',
      filters: {
        firm: 'Firma',
        kind: 'Tür',
        search: 'Personel, sicil ya da açan',
        searchPlaceholder: 'Ad, sicil ya da açan kişi…',
        all: 'Tümü',
      },
      bulk: {
        select: 'Talebi seç',
        selectAll: 'Onaylanabilir tümünü seç',
        approve: (count: number) => `Seçilenleri onayla (${count})`,
        confirmTitle: (count: number) => `${count} talep onaylansın mı?`,
        confirmBody:
          'Her talep ayrı doğrulanır; kaydı değişmiş olan onaylanmaz ve raporlanır. Onaylananlar DİA’ya ya da Passgage’e gönderilir.',
        resultTitle: 'Toplu onay sonucu',
        approved: (count: number) => `${count} talep onaylandı`,
        failed: (count: number) => `${count} talep onaylanmadı`,
        stale: 'Yeniden gönderilmeli: kayıt talep açıldıktan sonra değişti.',
        close: 'Kapat',
      },
      exportName: 'ik-onaylar',
      links: {
        movement: 'Hareketi aç',
        leave: 'İzni aç',
        person: 'Personeli aç',
      },
      openRequest: 'Talebi aç',
      manualAdd: 'Elle ekle',
      deleteMovement: {
        title: 'Giriş/çıkış silme talebi',
        targetsNote: 'Hedef: DİA. Onaydan sonra hareket DİA’dan silinir; bu adım geri alınmaz.',
        movement: 'Silinecek hareket',
        descriptionPlaceholder: 'Neden siliniyor?',
        submitDirect: 'Sil ve DİA’ya gönder',
      },
      actionFailed: 'İşlem tamamlanamadı',
      empty: 'Bu sekmede talep yok.',
    },
    interventions: {
      title: 'Müdahaleler',
      description:
        'Panelden yapılmış her müdahale: giriş/çıkış talepleri, kayıt aksiyonları ve elle çalıştırılan süreçler. Kim istedi, kim ne zaman onayladı ve DİA’ya gidişin sonucu; satıra tıklayınca geçmişi açılır.',
      tabs: {
        all: 'Tümü',
        talep: 'Talepler',
        aksiyon: 'Kayıt aksiyonları',
        surec: 'Süreç çalıştırma',
      } as Record<string, string>,
      searchPlaceholder: 'Personel, sicil, firma ya da kişi…',
      columns: {
        zaman: 'Zaman',
        mudahale: 'Müdahale',
        kayit: 'Kayıt',
        firma: 'Firma',
        yapan: 'Yapan',
        onay: 'Onay',
        sonuc: 'Sonuç',
      },
      source: { talep: 'Talep', aksiyon: 'Aksiyon' } as Record<string, string>,
      target: {
        hareket: 'Giriş/çıkış hareketi',
        izin: 'İzin',
        izin_duzeltme: 'İzin düzeltmesi',
        personel: 'Personel',
        surec: 'Süreç',
        talep: 'Talep',
      } as Record<string, string>,
      decision: {
        onaylandi: 'Onayladı',
        revizyon_istendi: 'Revizyona yolladı',
        iptal_edildi: 'İptal etti',
      } as Record<string, string>,
      direct: 'Onaysız işlendi',
      noApproval: 'Onay gerekmez',
      awaiting: 'Onay bekliyor',
      notSent: 'Gönderilmedi',
      requestedBy: 'İsteyen',
      requestedAt: 'İstenme',
      decidedBy: 'Karar',
      finishedAt: 'Bitiş',
      record: 'Kayıt',
      openRequest: 'Onaylar’da aç',
      count: (shown: number, total: number) =>
        shown === total ? `${total} müdahale` : `${shown} / ${total} müdahale`,
      empty: 'Henüz panelden yapılmış bir müdahale yok.',
      emptyFiltered: 'Bu süzgeçle eşleşen müdahale yok.',
    },
    tasks: {
      title: 'İşlerim',
      description:
        'Senkronun kendi çözemediği kayıtlar. Her satır bir karar bekliyor; düzeltip yeniden gönderin, yoksayın ya da DİA’da ne olacağını seçin. Yalnız yetkili olduğunuz firma ve şubeler görünür.',
      tabs: {
        all: 'Tümü',
        kalici_hata: 'Kalıcı hata',
        firma_belirsiz: 'Firma belirsiz',
        firma_bulunamadi: 'Firma bulunamadı',
        silinen_hareket: 'Passgage’de silinmiş',
        izin_durumu: 'İzin durumu değişti',
        izin_cakismasi: 'İzin çakışması',
      } as Record<string, string>,
      hint: {
        kalici_hata:
          'Üç denemede gönderilemedi; sistem artık kendisi denemiyor. Sorunu giderip “DİA’ya tekrar gönder” deyin ya da yoksayın.',
        firma_belirsiz:
          'Personelin hareket tarihinde farklı firmalarda birden çok aktif kartı var. DİA’da fazla kartı pasife alıp tekrar gönderin.',
        firma_bulunamadi:
          'Sicilin DİA’da personel kartı yok ya da kartın firması çözülemedi; kayıt firması bilinmeden DİA’ya gidemez. DİA’da kartı açın ya da düzeltin. Bu kayıtları yalnız İK onaycıları görür.',
        silinen_hareket:
          'Hareket DİA’ya aktarıldıktan sonra Passgage’de silindi. DİA’dan silinsin mi, kalsın mı?',
        izin_durumu:
          'Onaylı izin Passgage’de başka bir duruma geçti (ör. yeniden onayda). DİA’daki kayıt duruyor; Passgage’de karar verilince kendiliğinden güncellenir.',
        izin_cakismasi:
          'DİA, personelin o anı kapsayan bir izni varken giriş/çıkışı reddeder. Saatlik izni düzeltin ya da hareketi yoksayın.',
      } as Record<string, string>,
      columns: {
        zaman: 'Kayıt zamanı',
        kisi: 'Personel',
        firma: 'Firma',
        kayit: 'Kayıt',
        sorun: 'Sorun',
        since: 'Ne zamandan beri',
      },
      passgageStatus: {
        awaiting_approval: 'Onay bekliyor',
        hr_approval: 'İK onayında',
        on_administrator_approval: 'Yönetici onayında',
        document_delivery_awaiting: 'Belge bekliyor',
        cancellation_request_hr_approval: 'İptal talebi İK onayında',
        finished: 'Tamamlandı',
        approaching: 'Yaklaşıyor',
      } as Record<string, string>,
      deletedInPassgage: 'DİA’da duruyor, Passgage’de yok',
      attempts: (n: number) => `${n} deneme`,
      openRecord: 'Kaydı aç',
      count: (shown: number, total: number) =>
        shown === total ? `${total} iş` : `${shown} / ${total} iş`,
      empty: 'Yapılacak iş yok.',
      emptyTab: 'Bu sekmede iş yok.',
      diaDeleteConfirm: {
        title: 'Hareket DİA’dan silinsin mi?',
        body: 'Hareket DİA’dan kalıcı olarak silinir. O ayın bordrosu kapandıysa bordroyu etkileyebilir.',
      },
    },
  },

  notifications: {
    title: 'Bildirimler',
    open: (count: number) => (count ? `Bildirimler, ${count} okunmamış` : 'Bildirimler'),
    markAll: 'Tümünü okundu say',
    empty: 'Bildirim yok.',
    failed: 'Bildirimler yüklenemedi.',
  },

  /** Nakit planı (docs/NAKIT-PLANI-PLAN.md). Satır anahtarları motorun anahtarlarıdır. */
  cash: {
    weekly: {
      title: 'Haftalık Nakit',
      description:
        'DİA verisinden 13 hafta ve 6 aylık nakit akışı. Hücreye tıklayınca kaynak kalemler açılır.',
      firm: 'Firma',
      start: (date: string) => `Başlangıç ${date}`,
      cariSnapshot: (at: string) => `Cari raporu ${at}`,
      bankSnapshot: (at: string) => `Banka bakiyesi ${at}`,
      fxDate: (date: string) => `TCMB döviz alış kuru ${date}`,
      noFirms: 'Cari raporu çekilmiş firma yok.',
      noFirmsHint: 'Kapsamınızdaki firmalar için DİA cari raporu henüz çekilmemiş.',
      rowHeader: 'Kalem',
      total: 'Toplam',
      week: (end: string) => `Salı ${end}`,
      negative: 'Eksi bakiye',
      belowMin: 'Minimum nakit altında',
      negativeShort: 'Eksi',
      belowMinShort: 'Eşik altı',
      scenario: 'Senaryo',
      scenarios: { baz: 'Baz', kotumser: 'Kötümser' } as Record<string, string>,
      scenarioHint:
        "Kötümser: müşteri tahsilatları ve çek tahsilleri Nakit Ayarları'ndaki gün kadar geç gelir.",
    },
    sections: {
      bank: 'Banka',
      inflows: 'Girişler',
      outflows: 'Çıkışlar',
      cash: 'Kasa',
      summary: 'Toplam nakit',
      cheques: 'Çek ve senetler',
    },
    rows: {
      banka_acilis: 'Dönem başı banka',
      giris_tahsildeki_cek: 'Bankada tahsildeki çek ve senetler (vadesinde)',
      giris_portfoy_cek: 'Portföydeki çekler: ciro edilmeyen (vadesinde tahsile)',
      giris_pos: 'POS tahsilatları',
      giris_toplam: 'Toplam banka girişleri',
      cikis_tedarikci_grup_disi: 'Tedarikçi ödemeleri — grup dışı',
      cikis_tedarikci_grup_ici: 'Tedarikçi ödemeleri — grup şirketleri',
      cikis_verilen_cek: 'Verilmiş çeklerimiz (vadesi gelen)',
      cikis_kendi_cek: 'Kendi çekimizle planlanan ödemeler',
      cikis_kart_dia: 'Kredi kartı ekstreleri (DİA)',
      cikis_kart_planli: 'Kartla planlanan ödemelerin ekstresi',
      cikis_toplam: 'Toplam banka çıkışları',
      banka_kapanis: 'Dönem sonu banka',
      kasa_acilis: 'Dönem başı kasa',
      kasa_giris: 'Kasa tahsilatları',
      kasa_tedarikci: 'Tedarikçi ödemeleri — kasadan',
      kasa_erp_disi: 'ERP dışı ödemeler — kasadan',
      kasa_kapanis: 'Dönem sonu kasa',
      net_akis: 'Net nakit akışı (banka + kasa)',
      donem_sonu_nakit: 'Dönem sonu nakit (banka + kasa)',
      min_nakit: 'Minimum nakit seviyesi',
      portfoy_acilis: 'Portföy (ciro edilmemiş): dönem başı',
      portfoy_ciro: '− Ciro edilen (seçilen çekler)',
      portfoy_tahsile: '− Vadesi gelen → bankaya tahsile',
      portfoy_kapanis: 'Portföy (ciro edilmemiş): dönem sonu',
      tahsildeki_acilis: 'Bankada tahsildeki: dönem başı',
      tahsildeki_tahsil: '− Vadesi gelen (tahsil)',
      tahsildeki_kapanis: 'Bankada tahsildeki: dönem sonu',
      ciro_planlanan: 'Ciro ile planlanan ödemeler',
      ciro_fark: 'Fark: ciro ödemeleri − seçilen çekler',
      toplam_likidite: 'Toplam likidite',
    } as Record<string, string>,
    position: {
      title: 'Nakit Pozisyonu',
      bank: 'Banka',
      cash: 'Kasa',
      portfolio: 'Portföydeki çekler',
      inCollection: 'Bankada tahsildeki',
      total: 'Toplam likidite',
      accounts: 'Hesaplar',
    },
    drill: {
      description: (period: string) => `${period} · kaynak kalemler`,
      count: (n: number) => `${n} kalem`,
      total: 'Toplam',
      empty: 'Bu hücrede kalem yok.',
      date: 'Tarih',
      item: 'Kalem',
      amount: 'Tutar',
      sources: {
        cari: 'Cari',
        cek: 'Çek',
        pos: 'POS',
        verilen_cek: 'Verilen çek',
        kart: 'Kart',
        elle: 'ERP dışı',
      } as Record<string, string>,
    },
    strip: {
      title: 'Nakit şeridi',
      description: 'Önümüzdeki 13 haftanın dönem sonu nakdi (banka + kasa).',
      point: (end: string, amount: string) => `Salı ${end}: ${amount}`,
      negative: 'Eksi',
      belowMin: 'Eşik altı',
      threshold: (amount: string) => `Minimum nakit ${amount}`,
      idle: 'Tutarı görmek için bir haftanın üzerine gelin ya da dokunun.',
      showWeek: 'Haftalık tabloda göster',
      openWeekly: 'Haftalık Nakit’te aç',
    },
    summary: {
      title: 'Karar özeti',
      status: { good: 'Durum iyi', attention: 'Dikkat', problem: 'Sorun var' },
      reason: {
        good: (min: string) =>
          `Tüm haftalarda dönem sonu nakit minimum seviyenin (${min}) üstünde.`,
        attention: (weeks: number, min: string) =>
          `${weeks} haftada dönem sonu nakit minimum seviyenin (${min}) altında.`,
        problem: (weeks: number) => `${weeks} haftada dönem sonu nakit eksi.`,
      },
      lowest: 'En düşük dönem sonu nakit',
      lowestWeek: (end: string) => `Salı ${end} haftası`,
      negativeWeeks: 'Eksi hafta',
      weeks: (n: number) => `${n} hafta`,
      none: 'Yok',
      shortfall: 'Eşik açığı',
      shortfallHint: (min: string) => `Minimum nakit ${min}`,
      noShortfall: 'Eşiğin altına inmiyor',
      scenario: 'Kötümser fark',
      scenarioHint: 'Baz senaryoya göre en düşük nakit',
      scenarioNegative: (n: number) => `${n} eksi hafta daha`,
      firstBelowMin: (end: string) => `Nakit ${end} haftasında eşik altına iniyor.`,
    },
    planning: {
      title: 'Ödeme Planlama',
      description:
        'Cari bazında açık kalemler. Cariye girilen tutar en eski vadeli kalemden başlayarak dağıtılır.',
      search: 'Cari ara',
      onlyOpen: 'Yalnız ödenecek kalemi olanlar',
      open: 'Açık',
      planned: 'Planlanan',
      unplanned: 'Kurala kalan',
      items: (n: number) => `${n} kalem`,
      allocate: 'Cariye ödeme planla',
      amount: 'Tutar',
      date: 'Tarih',
      instrument: 'Ödeme aracı',
      submit: 'Dağıt',
      allocated: (n: number) => `Tutar ${n} kaleme dağıtıldı.`,
      saved: 'Plan kaydedildi.',
      failed: 'Kaydedilemedi.',
      due: 'Vade',
      doc: 'Fiş',
      type: 'Tür',
      klass: 'Sınıf',
      remainder: 'Kalan',
      remainderDate: 'Kalan ödeme',
      parts: 'Kısımlar',
      addPart: 'Kısım ekle',
      removePart: 'Kısmı sil',
      amountInvalid: 'Sıfırdan büyük bir tutar girin.',
      noteTooLong: 'Not en fazla 1000 karakter olabilir.',
      erpOutside: 'ERP dışında planlandı',
      note: 'Not',
      save: 'Kaydet',
      cancel: 'Vazgeç',
      edit: 'Düzenle',
      history: 'Geçmiş',
      historyTitle: 'Satır geçmişi',
      historyEmpty: 'Bu satırda değişiklik yok.',
      lastChange: (name: string, at: string) => `${name} · ${at}`,
      noCaris: 'Bu süzgeçte cari yok.',
      editTitle: 'Kalem planı',
      allocateTitle: 'Cariye tutar dağıt',
      columns: {
        cariCode: 'Cari kodu',
        cariName: 'Cari',
        erpOutside: 'ERP dışı',
        remainderInstrument: 'Kalan aracı',
        defaultInstrument: 'Kurala göre',
        control: 'Kontrol',
        updated: 'Son değişiklik',
      },
      grid: {
        label: 'Cari kalemler',
        hint: "Excel'den yapıştırmak için bir hücre seçip Ctrl+V. Ctrl+D aşağı doldurur, Delete temizler, Ctrl+Z geri alır. Kısımlar: gg.aa.yyyy tutar [araç]; …",
        save: (n: number) => (n ? `Kaydet (${n})` : 'Kaydet'),
        discard: 'Değişiklikleri at',
        saved: (n: number) => `${n} kalem planı kaydedildi.`,
        invalid: (n: number) => `${n} hücre hatalı; hiçbir satır kaydedilmedi.`,
        partsInvalid: 'Kısımlar "gg.aa.yyyy tutar [araç]; …" biçiminde olmalı.',
        instrumentUnknown: (name: string) => `Bilinmeyen ödeme aracı: ${name}`,
        classInvalid: 'Sınıf Ödeme, Avans, Kontrol ya da Hariç olmalı.',
        dateInvalid: 'Tarih GG.AA.YYYY biçiminde olmalı.',
        remainderRule: 'Kuraldan hesaplandı; elle girilmedi, kaydedilmez.',
        allocate: 'Dağıt',
        yes: 'Evet',
        no: 'Hayır',
      },
      controls: {
        kisimlar_fazla: 'Kısımlar kalandan fazla',
        kisim_tarihi_eksik: 'Kısım tarihi eksik',
      } as Record<string, string>,
      actions: {
        plan_guncellendi: 'Plan güncellendi',
        dagitim: 'Cari ödemesi dağıtıldı',
        ice_aktarim: "Excel'den taşındı",
        eklendi: 'Eklendi',
        guncellendi: 'Güncellendi',
        silindi: 'Silindi',
        kopyalandi: 'Kopyalandı',
        cekle_odeme: 'Çekle ödendi',
        ciro_geri_alindi: 'Ciro geri alındı',
        karar_guncellendi: 'Karar güncellendi',
        gerceklesti: 'Gerçekleşti',
        ertelendi: 'Ertelendi',
        iban_istendi: 'IBAN istendi',
        iban_yazildi: "IBAN DİA'ya yazıldı",
        iban_hatasi: 'IBAN yazılamadı',
      } as Record<string, string>,
    },
    manual: {
      title: 'ERP Dışı Kalemler',
      description:
        "DİA'da kaydı olmayan ödeme ve tahsilatlar. Düzenli kalem tahakkuk dönemiyle girilir; ödeme tarihi türün gününden hesaplanır.",
      add: 'Kalem ekle',
      editTitle: 'Kalemi düzenle',
      newTitle: 'Yeni ERP dışı kalem',
      source: 'Kaynak',
      sources: { duzenli: 'Düzenli', tek: 'Tek seferlik' } as Record<string, string>,
      type: 'Tür',
      descriptionField: 'Açıklama',
      period: 'Tahakkuk dönemi',
      date: 'Ödeme tarihi',
      day: 'Gün (isteğe bağlı)',
      amount: 'Tutar',
      instrument: 'Ödeme aracı',
      cariCode: 'Cari kodu (ciro için)',
      status: 'Durum',
      note: 'Dayanak / not',
      paymentDateValue: (date: string) => `Ödeme tarihi: ${date}`,
      paidBeforeStart: 'Başlangıçtan önce: ödenmiş sayılır',
      suggestion: (amount: string, basis: string) => `Öneri: ${amount} (${basis})`,
      basis: { previous: 'önceki ay', avg3: 'son 3 ay ortalaması' } as Record<string, string>,
      useSuggestion: 'Öneriyi kullan',
      copy: 'Sonraki aylara kopyala',
      copyMonths: 'Ay sayısı',
      copyIncrease: 'Artış (%)',
      copied: (n: number) => `${n} aya kopyalandı.`,
      delete: 'Sil',
      deleteConfirm: 'Kalem silinsin mi?',
      saved: 'Kalem kaydedildi.',
      deleted: 'Kalem silindi.',
      empty: 'ERP dışı kalem yok.',
      typeRequired: 'Bir tür seçin.',
      amountInvalid: 'Sıfırdan büyük bir tutar girin.',
      periodRequired: 'Düzenli kalem için tahakkuk dönemi seçin.',
      dateRequired: 'Tek seferlik kalem için ödeme tarihi seçin.',
      dayInvalid: 'Gün 1 ile 31 arasında bir tam sayı olmalı.',
      descriptionTooLong: 'Açıklama en fazla 300 karakter olabilir.',
      cariCodeTooLong: 'Cari kodu en fazla 50 karakter olabilir.',
      noteTooLong: 'Not en fazla 1000 karakter olabilir.',
      columns: {
        type: 'Tür',
        description: 'Açıklama',
        period: 'Dönem / tarih',
        payment: 'Ödeme',
        amount: 'Tutar',
        instrument: 'Araç',
        updated: 'Son değişiklik',
        source: 'Kaynak',
        day: 'Gün',
        cariCode: 'Cari kodu',
        note: 'Not',
      },
      grid: {
        label: 'ERP dışı kalemler',
        hint: "Excel'den yapıştırmak için bir hücre seçip Ctrl+V. Ctrl+D aşağı doldurur, Delete temizler, Ctrl+Z geri alır.",
        addRow: 'Satır ekle',
        save: (n: number) => (n ? `Kaydet (${n})` : 'Kaydet'),
        discard: 'Değişiklikleri at',
        saved: (n: number) => `${n} kalem kaydedildi.`,
        invalid: (n: number) => `${n} hücre hatalı; hiçbir satır kaydedilmedi.`,
        whenInvalid: 'Dönem AA.YYYY, tarih GG.AA.YYYY biçiminde olmalı.',
        paidBefore: 'Ödenmiş sayılır',
        removeNew: 'Satırı kaldır',
      },
    },
    settings: {
      title: 'Nakit Ayarları',
      description:
        'Firma bazında nakit planı kuralları. Varsayılanlar Mali İşler rehberinden gelir; değişiklik geçmişe yazılır.',
      defaults: 'Henüz kaydedilmedi: varsayılanlar gösteriliyor.',
      minCash: 'Minimum nakit seviyesi',
      weekday: 'Tedarikçi ödeme günü',
      weekdays: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma'],
      rule: 'Ödeme kuralı',
      deadlineDay: 'Firma girişlerinin son günü',
      deadlineTime: 'Son saat',
      pessimisticCollection: 'Kötümser senaryo: tahsilat gecikmesi (gün)',
      pessimisticCheque: 'Kötümser senaryo: çek vade kayması (gün)',
      noDeadline: 'Son gün yok',
      allWeekdays: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
      rules: {
        vadeden_sonraki_ilk: 'Vadeden sonraki ilk ödeme günü',
        vade_haftasindaki: 'Vadenin haftasındaki ödeme günü',
      } as Record<string, string>,
      types: 'ERP dışı kalem türleri',
      typeName: 'Tür',
      direction: 'Yön',
      day: 'Gün',
      monthOffset: 'Ödeme ayı farkı',
      classes: 'İşlem türü sınıfları',
      classCode: 'İşlem türü kodu',
      klass: 'Sınıf',
      addClass: 'Kod ekle',
      groupCaris: 'Grup şirketi carileri',
      groupCode: 'Cari kodu',
      groupName: 'Grup şirketi',
      addGroup: 'Cari ekle',
      remove: 'Kaldır',
      save: 'Ayarları kaydet',
      saved: 'Ayarlar kaydedildi.',
      minCashInvalid: 'Sıfır ya da daha büyük bir tutar girin.',
      typeDayInvalid: 'Gün 1 ile 31 arasında bir tam sayı olmalı.',
      monthOffsetInvalid: 'Ay farkı 0 ile 12 arasında bir tam sayı olmalı.',
      deadlineTimeInvalid: 'Saati SS:DD biçiminde girin.',
      pessimisticInvalid: 'Gün sayısı 0 ile 180 arasında bir tam sayı olmalı.',
      typesCount: 'En az 1, en fazla 60 tür olabilir.',
      lastChange: (name: string, at: string) => `Son değişiklik: ${name} · ${at}`,
      importTitle: "Excel'den tek seferlik taşıma",
      importDescription:
        "Mali İşler'in nakit planı Excel'indeki girişler (cari planları, çek kararları, ERP dışı kalemler, ayarlar) bir kez taşınır.",
      importFile: 'Excel dosyası',
      importReplace: 'Mevcut planlamanın yerine yaz',
      importRun: 'Taşı',
      imported: (n: { item_plans: number; recurring_items: number; one_off_items: number }) =>
        `${n.item_plans} cari planı, ${n.recurring_items} düzenli ve ${n.one_off_items} tek seferlik kalem taşındı.`,
    },
    cheques: {
      title: 'Çek Portföyü',
      description:
        'Portföydeki ve bankada tahsildeki müşteri çekleri. "Çekle öde" çeki cariye ciro eder ve tutarı carinin açık kalemlerine en eski vadeden dağıtır.',
      tabs: { portfoy: 'Portföyde', tahsil: 'Bankada tahsilde' } as Record<string, string>,
      columns: {
        due: 'Vade',
        serial: 'Seri no',
        bank: 'Banka',
        drawer: 'Keşideci',
        amount: 'Tutar',
        status: 'Durum',
        date: 'Tahsil / ciro',
        cari: 'Ciro edilecek cari',
      },
      status: {
        portfoyde: 'Portföyde',
        ciro_edilecek: 'Ciro edilecek',
        tahsile_verilecek: 'Tahsile verilecek',
        tahsilde: 'Tahsilde',
        haric: 'Hariç',
      } as Record<string, string>,
      endorse: 'Çekle öde',
      endorseTitle: (serial: string) => `Çekle öde: ${serial}`,
      cariSearch: 'Cari ara (kod ya da ünvan)',
      cariHint: 'En az 2 harf yazın. Ödeme planı olan cariler önce gelir.',
      cariNoOpen: 'Açık borcu yok',
      cariOpen: (amount: string) => `Planlanmamış ${amount}`,
      selectCari: (name: string) => `${name} carisini seç`,
      selected: (name: string) => `Seçili cari: ${name}`,
      date: 'Ciro tarihi',
      note: 'Not',
      submit: 'Ciro et ve dağıt',
      endorsed: (distributed: string) =>
        `Çek ciro edildi; ${distributed} carinin kalemlerine dağıtıldı.`,
      advance: (amount: string) =>
        `${amount} carinin açık bakiyesini aşıyor: ödeme değil avans olarak kalır.`,
      unendorse: 'Ciroyu geri al',
      unendorsed: 'Ciro geri alındı.',
      exclude: 'Hariç bırak',
      include: 'Dahil et',
      saved: 'Karar kaydedildi.',
      empty: 'Çek yok.',
    },
    health: {
      title: 'Veri Sağlığı',
      description:
        "Nakit tablosuna girmeyen ya da DİA'da düzeltme bekleyen kayıtlar. Her karar kim, ne zaman ve gerekçesiyle kaydedilir.",
      snapshots: 'Çekim anları',
      control: 'Kontrol sınıfı kalemler',
      controlHint: "Açılış fişi gibi kalemler nakde girmez; DİA'da temizlenmeli.",
      unknownCodes: 'Tanınmayan işlem türü kodları',
      unknownHint: "Sınıf eşlemesinde yok; Kontrol sayılıyor. Nakit Ayarları'ndan sınıf verin.",
      erpOutside: 'ERP dışında planlanan kalemler',
      pos: 'Bekleyen POS tahsilatları',
      posHint:
        "Valörü plan başlangıcından önce geçmiş bekleyenler varsayılan hariç (DİA'da kapatılmamış).",
      cards: 'Kart dönemleri',
      cardsHint: 'Son ödemesi geçmiş, ödemesi eşleşmemiş dönemler varsayılan hariç.',
      count: (n: number, amount: string) => `${n} kayıt · ${amount}`,
      overdue: (n: number, amount: string) => `Valörü geçmiş: ${n} kayıt · ${amount}`,
      excluded: 'Hariç',
      included: 'Dahil',
      byDefault: 'varsayılan',
      include: 'Dahil et',
      exclude: 'Hariç bırak',
      reset: 'Varsayılana dön',
      reason: 'Gerekçe',
      decided: (name: string, at: string) => `${name} · ${at}`,
      saved: 'Karar kaydedildi.',
      realized: '"Gerçekleşti mi?" kuyruğu',
      realizedHint:
        'Ödeme günü plan başlangıcından önceye düşen ERP dışı kalemler. Gerçekleştiyse işaretleyin; gerçekleşmediyse yeni gün verin.',
      realizedDone: 'Gerçekleşti',
      postpone: 'Ertele',
      newDate: 'Yeni ödeme günü',
      realizedEmpty: 'Bekleyen soru yok.',
      none: 'Kayıt yok.',
      columns: {
        date: 'Tarih',
        label: 'Kayıt',
        amount: 'Tutar',
        state: 'Durum',
        decision: 'Karar',
      },
    },
    unplanned: {
      title: 'Plana alınmayan',
      link: 'Veri Sağlığı',
      kinds: {
        kontrol: 'Kontrol sınıfı kalemler',
        haric: 'Hariç sınıfı kalemler',
        erp_disi: 'ERP dışında planlanan kalemler',
        pos_haric: 'Hariç bırakılan POS tahsilatları',
        kart_haric: 'Hariç bırakılan kart dönemleri',
        gecmis_elle: 'Gerçekleşti mi? bekleyen ERP dışı kalemler',
        grup_ici: 'Grup içi akışlar (konsolidede elendi)',
      } as Record<string, string>,
      row: (n: number, amount: string) => `${n} kayıt · ${amount}`,
    },
    paymentList: {
      title: 'Ödeme Listesi',
      description:
        'Seçilen salının ödeme talimatı: 6 bölüm, 5 ödeme aracı. Onaya gönderilen liste donar; her bölüm toplamı haftalık tabloyla karşılaştırılır.',
      week: 'Ödeme günü',
      status: {
        taslak: 'Taslak',
        onayda: 'Onayda',
        onaylandi: 'Onaylandı',
        odendi: 'Ödendi',
      } as Record<string, string>,
      frozen: 'Liste dondu: onaya gönderildiği andaki anlık görüntü.',
      submit: 'Onaya gönder',
      withdraw: 'Taslağa geri al',
      approve: 'Onayla',
      paid: 'Ödendi olarak işaretle',
      pdf: 'PDF indir',
      transitioned: (status: string) => `Liste: ${status}.`,
      by: (name: string, at: string) => `${name} · ${at}`,
      submitted: 'Onaya gönderen',
      approved: 'Onaylayan',
      paidBy: 'Ödendi işaretleyen',
      sendBack: 'Geri gönder',
      sendBackTitle: 'Listeyi geri gönder',
      sendBackHint:
        'Liste taslağa döner ve donması çözülür; planlayıcı düzeltmeyi bu nota göre yapar.',
      sendBackNote: 'Not',
      noteRequired: 'Geri gönderme nedenini yazın.',
      noteTooLong: 'Not en fazla 1000 karakter olabilir.',
      decisions: {
        title: 'Karar geçmişi',
        empty: 'Bu liste için henüz karar yok.',
        kind: {
          onaya_gonder: 'Onaya gönderdi',
          onayla: 'Onayladı',
          geri_gonder: 'Geri gönderdi',
          geri_cek: 'Taslağa geri aldı',
        } as Record<string, string>,
      },
      sections: {
        tedarikci: '1. Tedarikçi ödemeleri',
        erp_disi: '2. Vergi, SGK, maaş ve diğer ERP dışı ödemeler',
        verilen_cek: '3. Vadesi gelen verilmiş çekler',
        kart: '4. Kart ekstreleri',
        ciro_cek: '5. Ciro edilecek çekler',
        tahsilat: '6. Beklenen tahsilatlar',
      } as Record<string, string>,
      instruments: {
        banka: 'Banka',
        kasa: 'Kasa',
        ciro: 'Çek ciro',
        kendi_cek: 'Kendi çeki',
        kart: 'Kart',
      } as Record<string, string>,
      collections: {
        tahsildeki_cek: 'Bankada tahsildeki çekler',
        portfoy_cek: 'Portföy çekleri (tahsile)',
        pos: 'POS tahsilatları',
      } as Record<string, string>,
      date: 'Tarih',
      name: 'Ad',
      detail: 'Açıklama',
      iban: 'IBAN',
      total: 'Toplam',
      checkOk: '✔ Haftalık tabloyla aynı',
      checkBad: (diff: string) => `✖ Fark ${diff}`,
      overdue: 'Vadesi geçen',
      empty: 'Bu bölümde kalem yok.',
      ibanMissing: 'IBAN eksik',
      ibanChanged: 'IBAN değişti',
      ibanPending: "DİA'ya gönderiliyor",
      ibanDone: "DİA'ya yazıldı",
      ibanError: (msg: string) => `DİA hatası: ${msg}`,
      ibanInput: (name: string) => `${name} IBAN`,
      ibanSave: 'IBAN kaydet',
      ibanEdit: 'IBAN değiştir',
      ibanSaved: "IBAN DİA'ya gönderilmek üzere kuyruğa alındı.",
      filterIban: {
        eksik: 'Yalnız bankadan ödenecek ve IBAN eksik olanlar',
        hata: 'Yalnız IBAN hatalı olanlar',
      } as Record<string, string>,
    },
    home: {
      title: 'Bu Hafta Yapılacaklar',
      description:
        'Nakit planında bu hafta bekleyen işler. Her madde ilgili ekrana süzülmüş açılır.',
      empty: 'Bekleyen iş yok.',
      calm: 'Bu hafta bekleyen iş yok',
      calmHint: 'Listelenen kontrollerin hiçbirinde açık madde görünmüyor.',
      pending: (n: number) => `${n} madde bekliyor`,
      firm: (label: string) => `Firma: ${label}`,
      tones: { danger: 'Sorun', warning: 'Dikkat' } as Record<string, string>,
      amountLabel: {
        eksi_hafta: (amount: string) => `En düşük ${amount}`,
        ciro_farki: (amount: string) => `Fark ${amount}`,
      } as Record<string, ((amount: string) => string) | undefined>,
      firstWeek: (end: string) => `İlk hafta: Salı ${end}`,
      kinds: {
        liste_onay: 'Bu salının ödeme listesi onay bekliyor',
        iban_eksik: 'Bu hafta ödenecek tedarikçilerde IBAN eksik',
        iban_hata: "DİA'ya yazılamayan IBAN var",
        gerceklesti_mi: 'Gerçekleşti mi? sorusu bekleyen ERP dışı kalemler',
        pos_valoru_gecmis: 'Valörü geçmiş, karar verilmemiş POS tahsilatları',
        eksi_hafta: 'Dönem sonu nakdi eksi ya da minimumun altında haftalar',
        ciro_farki: 'Ciro planı ile seçilen çekler tutmuyor',
        tanimsiz_kod: 'Sınıfı tanımsız işlem türü kodları',
      } as Record<string, string>,
      count: (n: number) => `${n}`,
      open: 'Aç',
    },
    parallel: {
      title: 'Paralel Koşum',
      description:
        "Excel bırakılmadan önce: Mali İşler'in Excel'indeki haftalık tablo ile web'in aynı haftalar için hesapladığı değerler yan yana. İki ardışık haftada fark 0 olunca Excel kapanır.",
      file: 'Excel dosyası',
      run: 'Karşılaştır',
      sameStart: 'Excel ile web aynı başlangıçtan.',
      differentStart: (excel: string, web: string) =>
        `Başlangıçlar farklı (Excel ${excel}, web ${web}): fark beklenir; aynı gün alınmış Excel'le karşılaştırın.`,
      equal: '✔ Bütün satırlar ve haftalar 1 TL tolerans içinde aynı.',
      notEqual: (diff: string) => `✖ En büyük fark ${diff}`,
      onlyDiff: 'Yalnız farklı satırlar',
      excel: 'Excel',
      web: 'Web',
      row: 'Satır',
      maxDiff: 'En büyük fark',
    },
    variance: {
      title: 'Plan / Gerçekleşen',
      description:
        'Haftanın dondurulmuş tahmini ile DİA gerçekleşeni (banka fişleri ve kasa işlemleri), kategori ve cari bazında; tahmin doğruluğu ve geçen görüntüden bu yana değişenler.',
      week: 'Hafta (ödeme günü)',
      snapshot: 'Anlık görüntü al',
      snapshotTaken: 'Tahmin donduruldu.',
      noSnapshot:
        'Bu firma için dondurulmuş tahmin yok. Ödeme listesi onaya gönderilince, pazartesi 08:15\'te ya da "Anlık görüntü al" ile alınır.',
      snapshotInfo: (at: string, reason: string) => `Dondurulmuş tahmin: ${at} (${reason})`,
      reasons: {
        onay: 'ödeme listesi onayı',
        pazartesi: 'pazartesi',
        elle: 'elle',
        excel: "Excel'den taşıma",
      } as Record<string, string>,
      incomplete: 'Hafta bitmedi: gerçekleşen eksik.',
      categories: 'Kategoriler',
      category: 'Kategori',
      forecast: 'Tahmin',
      actual: 'Gerçekleşen',
      diff: 'Fark (tahmin − gerçekleşen)',
      diffShort: 'Fark',
      deviation: 'Sapma',
      directions: {
        forecast_higher: 'tahmin fazla',
        actual_higher: 'gerçekleşen fazla',
        equal: 'aynı',
      } as Record<string, string>,
      diffHint:
        'Fark = tahmin − gerçekleşen. Artı: tahmin gerçekleşenden yüksekti; eksi: gerçekleşen tahmini aştı. Hangisinin iyi olduğu kategoriye göre değişir (ör. giriş mi çıkış mı), bu yüzden renkle iyi/kötü denmez.',
      summary: {
        label: 'Dönem özeti',
        absoluteTotal: 'Toplam sapma (mutlak)',
        absoluteTotalHint:
          'Kategori farklarının mutlak değerleri toplanır. Tedarikçi ödemeleri banka çıkışlarıyla örtüşebilir.',
        topCategory: 'En çok sapan kategori',
        topCari: 'En çok sapan cari',
        exceeding: 'Eşiği aşan kategori',
        exceedingValue: (n: number, total: number) => `${n} / ${total}`,
        none: 'Sapma yok',
        deviationOf: (diff: string, direction: string) => `${diff} (${direction})`,
      },
      threshold: {
        label: 'Vurgu eşiği (%)',
        hint: 'Yalnız bu ekranda vurgu; kayıt ya da kural değildir.',
      },
      charts: {
        category: 'Kategori bazında tahmin ve gerçekleşen',
        accuracy: 'Tahmin doğruluğu: ufka göre',
        metricLabel: 'Ölçüt',
        mape: 'MAPE (%)',
        bias: 'Yanlılık (tahmin − gerçekleşen)',
        accuracyHint:
          'Ufuk, haftanın dondurulmuş görüntüdeki sırasıdır (1 = görüntünün ilk haftası). Yanlılık işareti fark ile aynıdır (tahmin − gerçekleşen).',
      },
      kinds: {
        banka_giris: 'Banka girişleri',
        banka_cikis: 'Banka çıkışları',
        tedarikci: 'Tedarikçi ödemeleri (banka + kasa)',
        kasa_net: 'Kasa net akışı',
      } as Record<string, string>,
      caris: 'Cari bazında tedarikçi ödemeleri',
      cari: 'Cari',
      accuracy: 'Tahmin doğruluğu (son 12 hafta)',
      horizon: 'Ufuk (hafta)',
      samples: 'Örnek',
      bias: 'Yanlılık',
      mape: 'MAPE',
      changes: 'Geçen görüntüden bu yana ne değişti',
      changesSince: (at: string) => `${at} tarihli görüntüye göre dönem sonu nakit`,
    },
    warnings: {
      missing_fx_rate: (code: string) =>
        `${code} için TCMB alış kuru bulunamadı; bu dövizdeki tutarlar 0 sayıldı.`,
      fx_unavailable: () => 'TCMB kurlarına ulaşılamadı; döviz tutarları 0 sayıldı.',
    },
  },

  /** Sürüm kapısı: başkası kaydı sizden sonra değiştirdi (409 `record_changed`, `lib/form/`). */
  recordConflict: {
    unknownUser: 'Başka bir kullanıcı',
    changed: (who: string, when: string | null) =>
      when ? `${who} ${when} değiştirdi` : `${who} değiştirdi`,
    title: 'Kaydedilemedi',
    description:
      'Değişiklikleriniz bu ekranda duruyor. Yeniden yüklerseniz atılır ve kaydın güncel hâli gelir.',
    reload: 'Yeniden yükle',
    reloadFailed: 'Kayıt yeniden yüklenemedi.',
    confirmTitle: 'Kaydı yeniden yükle',
    confirmText: 'Kaydedilmemiş değişiklikleriniz atılacak ve kaydın güncel hâli yüklenecek.',
  },

  /** Ortak form katmanı (`lib/form/use-record-editor.tsx`). */
  form: {
    unsavedTitle: 'Kaydedilmemiş değişiklikler',
    unsavedText: 'Kaydetmeden çıkarsanız bu formdaki değişiklikler kaybolur.',
    leaveWithoutSaving: 'Kaydetmeden çık',
    saveFailed: 'Kaydedilemedi',
  },

  errors: {
    network: 'Sunucuya ulaşılamadı.',
    unexpected: 'Beklenmeyen bir hata oluştu.',
    validation: 'Girdi doğrulanamadı.',
    unauthorized: 'Oturum gerekli.',
    forbidden: 'Bu işlem için yetkiniz yok.',
    notFound: 'Kayıt bulunamadı.',
    conflict: 'Çakışma oluştu.',
    upstream: 'Kaynak veritabanına erişilemedi.',
  },
} as const
